import axios from 'axios';

// Create the Nexus API instance for the Django backend
export const nexusApi = axios.create({
    baseURL: import.meta.env.VITE_NEXUS_API_URL,
    // 10 minutes timeout for local development, 4 minutes for production
    timeout: import.meta.env.DEV ? 600000 : 240000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Custom retry logic - no external dependency needed
nexusApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const config = error?.config;

        // No config means network-level failure (DNS, CORS) — nothing to retry
        if (!config) {
            return Promise.reject(error);
        }

        // Skip retry for specific requests if needed
        if (config.__skipRetry) {
            return Promise.reject(error);
        }

        // Initialize retry count
        config.__retryCount = config.__retryCount || 0;

        // Only retry if we haven't hit the max retries and it's a server error or network issue
        const shouldRetry = config.__retryCount < 3 && (!error.response || (error.response.status >= 500 && error.response.status < 600));

        if (shouldRetry) {
            config.__retryCount += 1;

            // Exponential backoff delay
            const delayMs = 1000 * Math.pow(2, config.__retryCount);

            // Wait for the delay
            await new Promise((resolve) => setTimeout(resolve, delayMs));

            // Return the axios instance to retry the request
            return nexusApi(config);
        }

        return Promise.reject(error);
    }
);

// Add request interceptor to automatically add the auth token and account ID to every request
nexusApi.interceptors.request.use(
    (config) => {
        // Let axios auto-set Content-Type for FormData (multipart/form-data with boundary)
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Token ${token}`;
        }

        // Add account ID header only for authenticated requests (not for auth endpoints)
        const isAuthEndpoint = config.url.includes('/auth/') || config.url.includes('/login') || config.url.includes('/firebase/');
        if (!isAuthEndpoint && token) {
            const currentAccount = localStorage.getItem('currentAccount');
            if (currentAccount) {
                try {
                    const account = JSON.parse(currentAccount);
                    if (account.id) {
                        config.headers['X-Account-ID'] = account.id;
                    }
                } catch (e) {
                    console.error('Error parsing current account:', e);
                }
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Firebase auth integration with Django backend
export const firebaseAuthService = {
    /**
     * Exchange Firebase ID token for Django token
     * @param {string} idToken - Firebase ID token
     * @returns {Promise} Promise with Django token response
     */
    exchangeToken: async (idToken) => {
        try {
            const response = await nexusApi.post('/firebase/auth/', { idToken });

            // Store the token and user data
            if (response.data.token) {
                localStorage.setItem('authToken', response.data.token);
                if (response.data.user) {
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                }

                // Try to get current account and admin status
                try {
                    const accountResponse = await nexusApi.get('/accounts/current/');
                    if (accountResponse.data) {
                        // Save the current account info
                        localStorage.setItem('currentAccount', JSON.stringify(accountResponse.data));
                        // Save admin status specifically for easy access
                        localStorage.setItem('isAccountAdmin', accountResponse.data.is_account_admin ? 'true' : 'false');
                    }
                } catch (accountError) {
                    // If no current account is selected or other error, clear admin status
                    localStorage.removeItem('currentAccount');
                    localStorage.setItem('isAccountAdmin', 'false');
                    console.log('No current account selected or error fetching account info:', accountError.response?.data?.error || accountError.message);
                }
            }

            return response.data;
        } catch (error) {
            console.error('Firebase token exchange error:', error);
            throw error;
        }
    },

    /**
     * Clear all auth data from localStorage
     */
    clearAuthData: () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('currentAccount');
        localStorage.removeItem('isAccountAdmin');
    }
};

// User-related API endpoints
export const userService = {
    /**
     * Get current user profile
     * @returns {Promise} Promise with user data
     */
    getProfile: () => nexusApi.get('/users/me/'),

    /**
     * Update user profile
     * @param {Object} userData - User data to update
     * @returns {Promise} Promise with updated user data
     */
    updateProfile: (userData) => nexusApi.patch('/users/me/', userData),

    /**
     * Change user password
     * @param {Object} passwordData - Object with old_password, new_password1, new_password2
     * @returns {Promise} Promise with result
     */
    changePassword: (passwordData) => nexusApi.post('/users/change-password/', passwordData)
};

// Email provider service
export const emailService = {
    /**
     * Get all email providers
     * @returns {Promise} Promise with providers list
     */
    getProviders: () => nexusApi.get('/mail/providers/'),

    /**
     * Get a specific email provider
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with provider details
     */
    getProvider: (id) => nexusApi.get(`/mail/providers/${id}/`),

    /**
     * Create a new email provider
     * @param {Object} data - Provider data
     * @returns {Promise} Promise with created provider
     */
    createProvider: (data) => nexusApi.post('/mail/providers/', data),

    /**
     * Update a provider
     * @param {number|string} id - Provider ID
     * @param {Object} data - Updated provider data
     * @returns {Promise} Promise with updated provider
     */
    updateProvider: (id, data) => nexusApi.put(`/mail/providers/${id}/`, data),

    /**
     * Partially update a provider
     * @param {number|string} id - Provider ID
     * @param {Object} data - Fields to update
     * @returns {Promise} Promise with updated provider
     */
    patchProvider: (id, data) => nexusApi.patch(`/mail/providers/${id}/`, data),

    /**
     * Delete a provider
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with result
     */
    deleteProvider: (id) => nexusApi.delete(`/mail/providers/${id}/`),

    /**
     * Test provider connection
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with connection test result
     */
    testConnection: (id) => nexusApi.post(`/mail/providers/${id}/test-connection/`),

    /**
     * Fetch emails from a provider
     * @param {Object} data - Fetch parameters (provider_id, limit, since_date)
     * @returns {Promise} Promise with fetch results
     */
    fetchEmails: (data) => nexusApi.post('/mail/fetch/', data),

    /**
     * Get emails
     * @param {Object} params - Query parameters
     * @returns {Promise} Promise with emails list
     */
    getEmails: (params) => nexusApi.get('/mail/emails/', { params }),

    /**
     * Get a specific email
     * @param {number|string} id - Email ID
     * @returns {Promise} Promise with email details
     */
    getEmail: (id) => nexusApi.get(`/mail/emails/${id}/`),

    /**
     * Get email messages with filtering and pagination
     * @param {Object} params - Query parameters (provider_id, sender, recipient, date_from, date_to, limit, offset)
     * @returns {Promise} Promise with messages list
     */
    getMessages: (params) => {
        // Add account_id from current account if not already in params
        const currentAccount = localStorage.getItem('currentAccount');
        if (currentAccount && !params.account_id) {
            try {
                const account = JSON.parse(currentAccount);
                if (account.id) {
                    params = { ...params, account_id: account.id };
                }
            } catch (e) {
                console.error('Error parsing current account:', e);
            }
        }
        return nexusApi.get('/mail/messages/', { params });
    },

    /**
     * Get a single email message by ID
     * @param {number|string} id - Message ID
     * @returns {Promise} Promise with message details
     */
    getMessage: (id) => {
        return nexusApi.get(`/mail/messages/${id}/`);
    },

    /**
     * Get admin email messages with filtering and pagination (admin only)
     * @param {Object} params - Query parameters (auto_fetch, provider_id, sender, recipient, date_from, date_to, limit, offset)
     * @returns {Promise} Promise with messages list
     */
    getAdminMessages: (params) => {
        return nexusApi.get('/mail/messages/admin/', { params });
    }
};

// CRM service for CRM providers and related endpoints
export const crmService = {
    /**
     * Get all CRM providers
     * @returns {Promise} Promise with providers list
     */
    getProviders: () => nexusApi.get('/crm/providers/'),

    /**
     * Get a specific CRM provider
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with provider details
     */
    getProvider: (id) => nexusApi.get(`/crm/providers/${id}/`),

    /**
     * Create a new CRM provider
     * @param {Object} data - Provider data
     * @returns {Promise} Promise with created provider
     */
    createProvider: (data) => nexusApi.post('/crm/providers/', data),

    /**
     * Update a provider
     * @param {number|string} id - Provider ID
     * @param {Object} data - Updated provider data
     * @returns {Promise} Promise with updated provider
     */
    updateProvider: (id, data) => nexusApi.put(`/crm/providers/${id}/`, data),

    /**
     * Partially update a provider
     * @param {number|string} id - Provider ID
     * @param {Object} data - Fields to update
     * @returns {Promise} Promise with updated provider
     */
    patchProvider: (id, data) => nexusApi.patch(`/crm/providers/${id}/`, data),

    /**
     * Delete a provider
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with result
     */
    deleteProvider: (id) => nexusApi.delete(`/crm/providers/${id}/`),

    /**
     * Sync cases modified on a specified date
     * @param {Object} data - Sync parameters (provider_id, date)
     * @returns {Promise} Promise with sync results
     */
    syncCases: (data) => nexusApi.post('/crm/sync-cases/', data),

    /**
     * Sync document categories from the CRM system
     * @param {Object} data - Sync parameters (provider_id)
     * @returns {Promise} Promise with sync results
     */
    syncDocumentCategories: (data) => nexusApi.post('/crm/sync-document-categories/', data),

    /**
     * Sync open cases from the CRM system
     * @param {Object} data - Sync parameters (provider_id)
     * @returns {Promise} Promise with sync results
     */
    syncOpenCases: (data) => nexusApi.post('/crm/sync-open-cases/', data),

    /**
     * Get all cases with optional pagination and filtering
     * @param {Object} params - Query parameters (page, page_size, search, status, case_type, etc.)
     * @returns {Promise} Promise with cases list and pagination metadata
     */
    getCases: (params = {}) => nexusApi.get('/crm/cases/', { params }),

    /**
     * Get filter options (case types and statuses) for case filtering
     * Cached on server for 1 hour
     * @returns {Promise} Promise with filter options { case_types: [], statuses: [] }
     */
    getCaseFilterOptions: () => nexusApi.get('/crm/cases/filter_options/'),

    /**
     * Get a specific case by case_number
     * @param {string} caseNumber - Case number
     * @returns {Promise} Promise with case details
     */
    getCase: (caseNumber, params = {}) => nexusApi.get(`/crm/cases/${encodeURIComponent(caseNumber)}/`, { params }),

    /**
     * Create a new case
     * @param {Object} data - Case data
     * @returns {Promise} Promise with created case
     */
    createCase: (data) => nexusApi.post('/crm/cases/', data),

    /**
     * Update a case
     * @param {number|string} id - Case ID
     * @param {Object} data - Updated case data
     * @returns {Promise} Promise with updated case
     */
    updateCase: (id, data) => nexusApi.put(`/crm/cases/${id}/`, data),

    /**
     * Delete a case
     * @param {number|string} id - Case ID
     * @returns {Promise} Promise with result
     */
    deleteCase: (id) => nexusApi.delete(`/crm/cases/${id}/`),

    /**
     * Sync a single case by its external ID from the CRM system
     * @param {Object} data - Object containing provider_id and case_id (external ID)
     * @returns {Promise} Promise with sync result
     */
    syncCaseById: (data) => nexusApi.post('/crm/task-sync-case-by-id/', data),

    /**
     * List Google Business Profiles available to the current account
     * @returns {Promise} Promise with [{id, name, review_link_url}]
     */
    listGoogleBusinessProfiles: () => nexusApi.get('/crm/google-business-profiles/'),

    /**
     * Send a Google review link via SMS or email.
     *
     * Exactly one channel (phone OR email) must be supplied per request — the
     * backend rejects payloads with both or neither with HTTP 400.
     *
     * Existing-contact, phone:
     *   { google_business_profile_id, contact_id, to_phone_number }
     * Existing-contact, email:
     *   { google_business_profile_id, contact_id, to_email_address }
     * New-contact, phone:
     *   { google_business_profile_id,
     *     new_contact: { first_name, last_name, phone_number, role } }
     * New-contact, email:
     *   { google_business_profile_id,
     *     new_contact: { first_name, last_name, email, role } }
     *
     * Note: the email field on the top-level payload is `to_email_address`
     * (not `to_email`).
     * @param {string} caseNumber - Case number
     * @param {Object} payload - See shapes above
     * @returns {Promise} Promise resolving with 201 on success
     */
    sendReviewLink: (caseNumber, payload) => nexusApi.post(`/crm/cases/${caseNumber}/send-review-link/`, payload),

    /**
     * List Google review links previously sent for a case (most recent first).
     * @param {string} caseNumber - Case number
     * @returns {Promise} Promise with [{id, sent_at, to_phone_number, to_email_address, gbp, to_contact, sent_by}]
     */
    listSentReviewLinks: (caseNumber) => nexusApi.get(`/crm/cases/${caseNumber}/review-links/`),

    /**
     * Get recent cases
     * @returns {Promise} Promise with list of recent cases
     */
    getRecentCases: () => nexusApi.get('/crm/recent-cases/'),

    /**
     * Get all case managers
     * @param {Object} params - Query parameters (search)
     * @returns {Promise} Promise with case managers list
     */
    getCaseManagers: (params = {}) => nexusApi.get('/crm/case-managers/', { params }),

    /**
     * Get a specific contact
     * @param {number|string} id - Contact ID
     * @returns {Promise} Promise with contact details
     */
    getContact: (id) => nexusApi.get(`/crm/contacts/${id}/`),

    /**
     * Create a new contact
     * @param {Object} data - Contact data
     * @returns {Promise} Promise with created contact
     */
    createContact: (data) => nexusApi.post('/crm/contacts/', data),

    /**
     * Update a contact
     * @param {number|string} id - Contact ID
     * @param {Object} data - Updated contact data
     * @returns {Promise} Promise with updated contact
     */
    updateContact: (id, data) => nexusApi.put(`/crm/contacts/${id}/`, data),

    /**
     * Delete a contact
     * @param {number|string} id - Contact ID
     * @returns {Promise} Promise with result
     */
    deleteContact: (id) => nexusApi.delete(`/crm/contacts/${id}/`),

    /**
     * Get all contact roles
     * @returns {Promise} Promise with contact roles list
     */
    getContactRoles: () => nexusApi.get('/crm/contact-roles/'),

    /** Get all phone types. */
    getPhoneTypes: () => nexusApi.get('/crm/phone-types/'),

    /**
     * Set or clear the do-not-call flag on a contact phone.
     * @param {number|string} phoneId - Contact phone ID
     * @param {boolean} value - New do-not-call value
     * @returns {Promise} Promise with the updated phone
     */
    setPhoneDoNotCall: (phoneId, value) => nexusApi.post(`/crm/contact-phones/${phoneId}/do-not-call/`, { do_not_call: value }),

    /**
     * Get all provider roles
     * @returns {Promise} Promise with provider roles list
     */
    getProviderRoles: () => nexusApi.get('/crm/provider-roles/'),

    /**
     * Get all provider specialties
     * @returns {Promise} Promise with provider specialties list
     */
    getProviderSpecialties: () => nexusApi.get('/crm/provider-specialties/'),

    /**
     * Get provider contacts (contacts where is_provider=True).
     * Distinct from getProviders, which lists CRM source systems (CRMProvider).
     * @param {Object} params - Query parameters (search, role, specialty, provider, page, page_size)
     * @returns {Promise} Promise with paginated provider contacts list
     */
    getProviderContacts: (params = {}) => nexusApi.get('/crm/contacts/providers/', { params }),

    /**
     * Get non-provider contacts (contacts where is_provider=False)
     * @param {Object} params - Query parameters (search, role, provider, page, page_size)
     * @returns {Promise} Promise with paginated contacts list
     */
    getContacts: (params = {}) => nexusApi.get('/crm/contacts/contacts/', { params }),

    /**
     * Get a contact by external_id
     * @param {string} externalId - The external ID of the contact
     * @returns {Promise} Promise with contact details
     */
    getContactByExternalId: (externalId) => nexusApi.get('/crm/contacts/by_external_id/', { params: { external_id: externalId } }),

    /**
     * Get a case by external ID
     * @param {string} externalId - Case external ID
     * @returns {Promise} Promise with case details including case number and name
     */
    getCaseByExternalId: (externalId) => nexusApi.get(`/crm/cases/external/${externalId}/`),

    // Case workflow methods
    /**
     * Get available workflows for a case
     * @param {number} accountId - Account ID
     * @param {number} caseId - Case ID
     * @returns {Promise} Promise with available workflow templates
     */
    getCaseWorkflows: (accountId, caseId) => nexusApi.get(`/case_processing/accounts/${accountId}/cases/${caseId}/workflows/`),

    /**
     * Get workflow execution history for a case
     * @param {number} accountId - Account ID
     * @param {number} caseId - Case ID
     * @returns {Promise} Promise with workflow executions
     */
    getCaseWorkflowExecutions: (accountId, caseId) => nexusApi.get(`/case_processing/accounts/${accountId}/cases/${caseId}/workflows/executions/`),

    /**
     * Trigger a workflow for a case
     * @param {number} accountId - Account ID
     * @param {number} caseId - Case ID
     * @param {Object} data - Workflow trigger data (template_id, input_parameters)
     * @returns {Promise} Promise with trigger result
     */
    triggerWorkflow: (accountId, caseId, data) => nexusApi.post(`/case_processing/accounts/${accountId}/cases/${caseId}/workflows/trigger/`, data),

    /**
     * Get workflow execution details
     * @param {number} accountId - Account ID
     * @param {number} executionId - Execution ID
     * @returns {Promise} Promise with execution details
     */
    getWorkflowExecution: (accountId, executionId) => nexusApi.get(`/case_processing/accounts/${accountId}/workflow-executions/${executionId}/`),

    /**
     * Cancel a running workflow execution
     * @param {number} accountId - Account ID
     * @param {number} executionId - Execution ID
     * @returns {Promise} Promise with cancellation result
     */
    cancelWorkflowExecution: (accountId, executionId) => nexusApi.post(`/case_processing/accounts/${accountId}/workflow-executions/${executionId}/cancel/`),

    // Batch workflow methods
    /**
     * Get all available workflow templates
     * @param {number} accountId - Account ID
     * @returns {Promise} Promise with workflow templates list
     */
    getWorkflowTemplates: (accountId) => nexusApi.get(`/case_processing/accounts/${accountId}/templates/`),

    /**
     * Preview cases for batch workflow execution
     * @param {number} accountId - Account ID
     * @param {Object} filters - Case filter criteria
     * @returns {Promise} Promise with filtered cases and count
     */
    previewBatchCases: (accountId, filters) => nexusApi.post(`/case_processing/accounts/${accountId}/batch/preview/`, filters),

    /**
     * Execute batch workflow on filtered cases
     * @param {number} accountId - Account ID
     * @param {Object} data - Batch execution data (template_id, filters, batch_name)
     * @returns {Promise} Promise with batch execution result
     */
    executeBatchWorkflow: (accountId, data) => nexusApi.post(`/case_processing/accounts/${accountId}/batch/execute/`, data),

    /**
     * Get batch workflow execution history
     * @param {number} accountId - Account ID
     * @returns {Promise} Promise with batch history
     */
    getBatchHistory: (accountId) => nexusApi.get(`/case_processing/accounts/${accountId}/batch/history/`),

    /**
     * Get batch workflow execution details
     * @param {number} accountId - Account ID
     * @param {number} batchId - Batch ID
     * @returns {Promise} Promise with batch execution details
     */
    getBatchDetails: (accountId, batchId) => nexusApi.get(`/case_processing/accounts/${accountId}/batch/${batchId}/`),

    /**
     * Get executions for a batch with case details
     * @param {number} accountId - Account ID
     * @param {number} batchId - Batch ID
     * @returns {Promise} Promise with batch executions and case details
     */
    getBatchExecutions: (accountId, batchId) => nexusApi.get(`/case_processing/accounts/${accountId}/batch/${batchId}/executions/`),

    /**
     * Cancel a running batch workflow execution
     * @param {number} accountId - Account ID
     * @param {number} batchId - Batch ID
     * @returns {Promise} Promise with cancellation result
     */
    cancelBatchExecution: (accountId, batchId) => nexusApi.post(`/case_processing/accounts/${accountId}/batch/${batchId}/cancel/`),

    /**
     * Get latest lien reconciliation for a case
     * @param {number} accountId - Account ID
     * @param {number} caseId - Case ID
     * @returns {Promise} Promise with lien reconciliation data
     */
    getLatestLienReconciliation: (accountId, caseId) => nexusApi.get(`/case_processing/accounts/${accountId}/cases/${caseId}/lien-reconciliation/latest/`),

    /**
     * Update lien reconciliation data for a case
     * @param {number} accountId - Account ID
     * @param {number} caseId - Case ID
     * @param {Object} lienData - Updated lien reconciliation data
     * @returns {Promise} Promise with update result
     */
    updateLienReconciliation: (accountId, caseId, lienData) => nexusApi.put(`/case_processing/accounts/${accountId}/cases/${caseId}/lien-reconciliation/latest/`, lienData),

    /**
     * Get notes for a case with pagination
     * @param {number} accountId - Account ID
     * @param {number} caseId - Case ID
     * @param {Object} params - Query parameters (page, page_size, topic, search)
     * @returns {Promise} Promise with paginated notes list
     */
    getCaseNotes: (accountId, caseId, params = {}) => nexusApi.get(`/crm/accounts/${accountId}/cases/${caseId}/notes/`, { params }),

    /**
     * Get calendar events for a case, optionally filtered by date range
     * @param {number} accountId - Account ID
     * @param {number} caseId - Case ID
     * @param {Object} params - Query parameters (start, end ISO datetimes)
     * @returns {Promise} Promise with list of calendar events
     */
    getCaseCalendarEvents: (accountId, caseId, params = {}) => nexusApi.get(`/crm/accounts/${accountId}/cases/${caseId}/calendar-events/`, { params }),

    /**
     * Get attribution data (originating lead + touchpoint journey) for a case
     * @param {number} accountId - Account ID
     * @param {number} caseId - Case ID
     * @returns {Promise} Promise with { lead, touchpoints } payload
     */
    getCaseAttribution: (accountId, caseId) => nexusApi.get(`/crm/accounts/${accountId}/cases/${caseId}/attribution/`),

    /**
     * Trigger batch processing for unprocessed documents linked to a specific case
     * @param {number} accountId - Account ID
     * @param {number} caseId - Case ID
     * @param {Object} data - Optional data (limit, dry_run)
     * @returns {Promise} Promise with processing results
     */
    triggerCaseDocumentProcessing: (accountId, caseId, data = {}) => nexusApi.post(`/case_processing/accounts/${accountId}/cases/${caseId}/trigger-document-processing/`, data),

    /**
     * Get staff assignment analytics
     * @param {Object} params - Query parameters (staff_ids, case_types, role_ids, start_date, end_date)
     * @returns {Promise} Promise with analytics data
     */
    getStaffAssignmentAnalytics: (params) => nexusApi.get('/crm/staff-assignment-analytics/', { params }),

    /**
     * Get list of all active staff members
     * @returns {Promise} Promise with staff list
     */
    getStaff: () => nexusApi.get('/crm/staff/'),

    /**
     * Get list of all active staff roles
     * @returns {Promise} Promise with staff roles list
     */
    getStaffRoles: () => nexusApi.get('/crm/staff-roles/')
};

// Add this to your nexus.js file

// Document processing service
export const documentService = {
    /**
     * Upload a document file
     * @param {File} file - File to upload
     * @param {Object} options - Additional options
     * @returns {Promise} Promise with upload result
     */
    uploadDocument: (file, options = {}) => {
        const formData = new FormData();
        formData.append('file', file);

        return nexusApi.post('/documents/upload/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            ...options
        });
    },

    /**
     * Get a specific document by ID
     * @param {number|string} id - Document ID
     * @returns {Promise} Promise with the document details
     */
    getDocument: (id) => nexusApi.get(`/documents/inbounds/${id}/document/${id}/`),

    /**
     * Get document content URL (proxy endpoint)
     * @param {number|string} inboundId - Inbound ID
     * @param {number|string} documentId - Document ID
     * @returns {string} URL to access document content through proxy
     */
    getDocumentContentUrl: (inboundId, documentId) => {
        const baseURL = nexusApi.defaults.baseURL;
        // Remove trailing slash from baseURL if present
        const cleanBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
        return `${cleanBaseURL}/documents/inbounds/${inboundId}/document/${documentId}/content/`;
    },

    /**
     * Get document content URL directly by document ID (without inbound)
     * @param {number|string} documentId - Document ID
     * @returns {string} URL to access document content through proxy
     */
    getDocumentContentUrlDirect: (documentId) => {
        const baseURL = nexusApi.defaults.baseURL;
        // Remove trailing slash from baseURL if present
        const cleanBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;
        return `${cleanBaseURL}/documents/documents/${documentId}/content/`;
    },

    /**
     * Get document preview data (for handling auth headers)
     * @param {number|string} inboundId - Inbound ID
     * @param {number|string} documentId - Document ID
     * @returns {Promise} Promise with document data
     */
    getDocumentPreview: (inboundId, documentId) =>
        nexusApi.get(`/documents/inbounds/${inboundId}/document/${documentId}/content/`, {
            responseType: 'blob'
        }),

    /**
     * Get document content as blob directly by document ID (without inbound)
     * @param {number|string} documentId - Document ID
     * @returns {Promise} Promise with document blob data
     */
    getDocumentContentBlobDirect: (documentId) =>
        nexusApi.get(`/documents/documents/${documentId}/content/`, {
            responseType: 'blob'
        }),

    /**
     * Get document content as blob via inbound
     * @param {number|string} inboundId - Inbound ID
     * @param {number|string} documentId - Document ID
     * @returns {Promise} Promise with document blob data
     */
    getDocumentContentBlob: (inboundId, documentId) =>
        nexusApi.get(`/documents/inbounds/${inboundId}/document/${documentId}/content/`, {
            responseType: 'blob'
        }),

    /**
     * Soft-unlink a document from a case (idempotent). The link is retired to
     * UNLINKED_MANUAL; when the document holds a synced Neos copy the backend
     * also attempts a CRM delete. Response reports crm_delete_attempted /
     * crm_deleted / already_unlinked so the UI can warn on a failed delete.
     * @param {number|string} documentId - Document ID
     * @param {number|string} caseId - Case ID to unlink from
     * @param {string} [notes] - Optional audit note appended to the link
     * @returns {Promise} Promise with unlink result
     */
    unlinkCase: (documentId, caseId, notes) => nexusApi.post(`/documents/${documentId}/unlink-case/`, notes ? { case_id: caseId, notes } : { case_id: caseId }),

    /**
     * Get all inbounds with optional filtering
     * @param {Object} params - Query parameters (like status)
     * @returns {Promise} Promise with inbounds list
     */
    getInbounds: (params = {}) => nexusApi.get('/documents/inbounds/', { params }),

    /**
     * Get a specific inbound
     * @param {number|string} id - Inbound ID
     * @returns {Promise} Promise with inbound details
     */
    getInbound: (id) => nexusApi.get(`/documents/inbounds/${id}/`),

    /**
     * Create a new inbound
     * @param {Object} data - Inbound data
     * @returns {Promise} Promise with created inbound
     */
    createInbound: (data) => nexusApi.post('/documents/inbounds/', data),

    /**
     * Update an inbound
     * @param {number|string} id - Inbound ID
     * @param {Object} data - Updated inbound data
     * @returns {Promise} Promise with updated inbound
     */
    updateInbound: (id, data) => nexusApi.put(`/documents/inbounds/${id}/`, data),

    /**
     * Delete an inbound
     * @param {number|string} id - Inbound ID
     * @returns {Promise} Promise with result
     */
    deleteInbound: (id) => nexusApi.delete(`/documents/inbounds/${id}/`),

    /**
     * Update the status of an inbound message
     * @param {number|string} inboundId - Inbound ID
     * @param {string} status - New status value
     * @returns {Promise} Promise with update result
     */
    updateInboundStatus: (inboundId, status) => nexusApi.patch(`/documents/inbounds/${inboundId}/`, { status }),

    /**
     * Get documents for an inbound
     * @param {number|string} id - Inbound ID
     * @returns {Promise} Promise with documents list
     */
    getInboundDocuments: (id) => nexusApi.get(`/documents/inbounds/${id}/documents/`),

    /**
     * Attach a document to an inbound
     * @param {number|string} inboundId - Inbound ID
     * @param {number|string} documentId - Document ID to attach
     * @returns {Promise} Promise with result
     */
    attachDocument: (inboundId, documentId) => nexusApi.post(`/documents/inbounds/${inboundId}/attach_document/`, { document_id: documentId }),

    /**
     * Get a document PDF by ID
     * @param {number|string} inboundId - Inbound ID
     * @param {number|string} documentId - Document ID
     * @returns {Promise} Promise with the signed URL response
     */
    getDocumentPDF: (inboundId, documentId) => nexusApi.get(`/documents/inbounds/${inboundId}/document/${documentId}/pdf/`),

    /**
     * Get a document PDF signed URL directly by document ID (without inbound)
     * @param {number|string} documentId - Document ID
     * @returns {Promise} Promise with the signed URL response
     */
    getDocumentPDFDirect: (documentId) => nexusApi.get(`/documents/documents/${documentId}/pdf/`),

    /**
     * Get a document PDF content as ArrayBuffer for PDF.js
     * @param {number|string} inboundId - Inbound ID
     * @param {number|string} documentId - Document ID
     * @returns {Promise} Promise with the PDF ArrayBuffer
     */
    getDocumentPDFContent: (inboundId, documentId) =>
        nexusApi.get(`/documents/inbounds/${inboundId}/document/${documentId}/content/`, {
            responseType: 'arraybuffer'
        }),

    /**
     * Get a document PDF content directly as ArrayBuffer for PDF.js (without inbound)
     * @param {number|string} documentId - Document ID
     * @returns {Promise} Promise with the PDF ArrayBuffer
     */
    getDocumentPDFContentDirect: (documentId) =>
        nexusApi.get(`/documents/documents/${documentId}/content/`, {
            responseType: 'arraybuffer'
        }),

    /**
     * Get document details
     * @param {number|string} inboundId - Inbound ID
     * @param {number|string} documentId - Document ID
     * @returns {Promise} Promise with document details
     */
    getDocumentDetail: (inboundId, documentId) => nexusApi.get(`/documents/inbounds/${inboundId}/document/${documentId}/`),

    /**
     * Get a specific inbound with context
     * @param {number|string} id - Inbound ID
     * @returns {Promise} Promise with inbound details including context
     */
    getInboundWithContext: (id) => nexusApi.get(`/documents/inbounds/${id}/?include_context=true`),

    /**
     * Find unprocessed documents with pagination
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (default 1)
     * @param {number} params.page_size - Page size (default 25, max 500)
     * @param {number} params.limit - Legacy limit parameter for backward compatibility
     * @returns {Promise} Promise with paginated unprocessed documents list
     */
    findUnprocessedDocuments: (params = {}) => nexusApi.get('/documents/find-unprocessed/', { params }),

    /**
     * Create a batch processing task for a document
     * @param {Object} data - Task data
     * @param {number} data.document_id - Document ID to process
     * @param {number} data.inbound_id - Optional inbound ID (will create one if not provided)
     * @returns {Promise} Promise with created task details
     */
    createBatchTask: (data) => nexusApi.post('/documents/batch/create-task/', data),

    /**
     * Get batch processing metrics
     * @returns {Promise} Promise with queue and workflow metrics
     */
    getBatchMetrics: () => nexusApi.get('/documents/batch/metrics/'),

    /**
     * Get batch processing inbounds with case information
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number
     * @param {number} params.page_size - Page size
     * @param {string} params.status - Filter by status
     * @param {string} params.date_from - Filter by start date (ISO datetime)
     * @param {string} params.date_to - Filter by end date (ISO datetime)
     * @returns {Promise} Promise with paginated batch processing inbounds
     */
    getBatchProcessingInbounds: (params = {}) => nexusApi.get('/documents/inbounds/batch_processing/', { params }),

    /**
     * Schedule retry for case matching
     * @param {number|string} inboundId - Inbound ID
     * @param {string} retryOption - Retry option (tomorrow, 2_days, 1_week)
     * @returns {Promise} Promise with retry scheduling result
     */
    scheduleRetry: (inboundId, retryOption) => nexusApi.post(`/documents/inbounds/${inboundId}/schedule-retry/`, { retry_option: retryOption })
};

// Processing steps service
export const stepsService = {
    /**
     * Initialize context for an inbound document
     * @param {number|string} inboundId - Inbound ID
     * @returns {Promise} Promise with context initialization result
     */
    initializeContext: (inboundId) => nexusApi.post(`/documents/inbounds/${inboundId}/initialize-context/`),

    /**
     * Check eligibility for an inbound document
     * @param {number|string} inboundId - Inbound ID
     * @returns {Promise} Promise with eligibility check result
     */
    checkEligibility: (inboundId) => nexusApi.post(`/documents/inbounds/${inboundId}/check-eligibility/`),

    /**
     * Check for duplicates of an inbound document
     * @param {number|string} inboundId - Inbound ID
     * @returns {Promise} Promise with duplicate check result
     */
    checkDuplicates: (inboundId) => nexusApi.post(`/documents/inbounds/${inboundId}/check-duplicates/`),

    /**
     * Download documents for an inbound
     * @param {number|string} inboundId - Inbound ID
     * @returns {Promise} Promise with document download result
     */
    downloadDocuments: (inboundId) => nexusApi.post(`/documents/inbounds/${inboundId}/download-documents/`),

    /**
     * Extract pages from all documents in an inbound
     * @param {number|string} inboundId - Inbound ID
     * @param {Object} options - Optional parameters like project_id, location, processor_id
     * @returns {Promise} Promise with extraction results
     */
    extractPages: (inboundId, options = {}) => nexusApi.post(`/documents/inbounds/${inboundId}/extract-pages/`, options),

    /**
     * Classify documents associated with an inbound
     * @param {number|string} inboundId - Inbound ID
     * @param {Object} options - Optional parameters like classifier_type, max_pdf_pages
     * @returns {Promise} Promise with classification results
     */
    classifyDocuments: (inboundId, options = {}) => nexusApi.post(`/documents/inbounds/${inboundId}/classify-documents/`, options),

    /**
     * Extract contacts from documents associated with an inbound
     * @param {number|string} inboundId - Inbound ID
     * @param {Object} options - Optional parameters like prompt_type
     * @returns {Promise} Promise with contact extraction results
     */
    extractContacts: (inboundId, options = {}) => nexusApi.post(`/documents/inbounds/${inboundId}/extract-contacts/`, options),

    /**
     * Summarize documents associated with an inbound
     * @param {number|string} inboundId - Inbound ID
     * @param {Object} options - Optional parameters like prompt_type, summary_prompt_type
     * @returns {Promise} Promise with summarization results
     */
    summarizeDocuments: (inboundId, options = {}) => nexusApi.post(`/documents/inbounds/${inboundId}/summarize-documents/`, options),

    /**
     * Create short summaries of documents associated with an inbound
     * @param {number|string} inboundId - Inbound ID
     * @param {Object} options - Optional parameters
     * @returns {Promise} Promise with short summarization results
     */
    shortSummarizeDocuments: (inboundId, options = {}) => nexusApi.post(`/documents/inbounds/${inboundId}/short-summarize-documents/`, options),

    /**
     * Match documents in an inbound to relevant cases
     * @param {number|string} inboundId - Inbound ID
     * @param {Object} options - Optional parameters like matcher_type
     * @returns {Promise} Promise with case matching results
     */
    matchCases: (inboundId, options = {}) => nexusApi.post(`/documents/inbounds/${inboundId}/match-cases/`, options),

    /**
     * Get prompt results for all documents in an inbound
     * @param {number|string} inboundId - Inbound ID
     * @param {Object} params - Query parameters like prompt_type, status
     * @returns {Promise} Promise with prompt results
     */
    getPromptResults: (inboundId, params = {}) => nexusApi.get(`/documents/inbounds/${inboundId}/prompt-results/`, { params }),

    /**
     * Sync a document to the CRM system
     * @param {number|string} inboundId - Inbound ID
     * @param {number|string} documentId - Document ID
     * @param {Object} options - Optional parameters like provider_id
     * @returns {Promise} Promise with sync results
     */
    syncDocumentToCRM: (inboundId, documentId, options = {}) => nexusApi.post(`/documents/inbounds/${inboundId}/document/${documentId}/sync-to-crm/`, options),

    /**
     * Update a document metadata in the CRM system
     * @param {number|string} inboundId - Inbound ID
     * @param {number|string} documentId - Document ID
     * @param {Object} options - Optional parameters like provider_id, subject
     * @returns {Promise} Promise with update results
     */
    updateDocumentInCRM: (inboundId, documentId, options = {}) => nexusApi.post(`/documents/inbounds/${inboundId}/document/${documentId}/update-in-crm/`, options),

    /**
     * Sync all documents in an inbound to the CRM system
     * @param {number|string} inboundId - Inbound ID
     * @param {Object} options - Optional parameters like provider_id
     * @returns {Promise} Promise with sync results
     */
    syncInboundToCRM: (inboundId, options = {}) => nexusApi.post(`/documents/inbounds/${inboundId}/sync-to-crm/`, options),

    /**
     * Execute workflow for an inbound document
     * @param {number|string} inboundId - Inbound ID
     * @param {Object} options - Optional parameters for workflow execution
     * @returns {Promise} Promise with workflow execution result
     */
    executeWorkflow: (inboundId, options = {}) => nexusApi.post(`/documents/inbounds/${inboundId}/execute-workflow/`, options),

    /**
     * Cancel a workflow execution
     * @param {number|string} inboundId - Inbound ID
     * @param {number|string} executionId - Workflow Execution ID
     * @returns {Promise} Promise with cancellation result
     */
    cancelWorkflow: (inboundId, executionId) => nexusApi.post(`/documents/inbounds/${inboundId}/workflows/${executionId}/cancel/`),

    /**
     * Refresh all workflow executions for an inbound from GCP
     * @param {number|string} inboundId - Inbound ID
     * @returns {Promise} Promise with refreshed workflow executions
     */
    refreshWorkflows: (inboundId) => nexusApi.post(`/documents/inbounds/${inboundId}/refresh-workflows/`),

    /**
     * Generate filenames for documents associated with an inbound
     * @param {number|string} inboundId - Inbound ID
     * @param {Object} options - Optional parameters like prompt_type
     * @returns {Promise} Promise with filename generation results
     */
    generateFilenames: (inboundId, options = {}) => nexusApi.post(`/documents/inbounds/${inboundId}/generate-filenames/`, options),

    /**
     * Create a document-case link
     * @param {Object} payload - Link data (document_id, case_id, inbound_id)
     * @returns {Promise} Promise with created link details
     */
    createCaseLink: (payload) => nexusApi.post('/documents/create-case-link/', payload),

    /**
     * Send notification about an inbound document
     * @param {number|string} inboundId - Inbound ID
     * @param {Object} options - Optional parameters like workflow_name, message, context, notification_type
     * @returns {Promise} Promise with notification result
     */
    sendInboundNotification: (inboundId, options = {}) => nexusApi.post(`/documents/inbounds/${inboundId}/send-notification/`, options),

    /**
     * Get potential actions for a document
     * @param {number|string} inboundId - Inbound ID
     * @param {number|string} documentId - Document ID
     * @param {Object} params - Query parameters (status, priority)
     * @returns {Promise} Promise with potential actions list
     */
    getDocumentPotentialActions: (inboundId, documentId, params = {}) => nexusApi.get(`/documents/inbounds/${inboundId}/document/${documentId}/potential-actions/`, { params }),

    /**
     * Update a potential action (approve, edit, discard)
     * @param {number|string} actionId - Action ID
     * @param {Object} payload - Update payload with operation and other data
     * @returns {Promise} Promise with updated action
     */
    updatePotentialAction: (actionId, payload) => nexusApi.patch(`/documents/potential-actions/${actionId}/`, payload),

    /**
     * Execute an approved potential action
     * @param {number|string} actionId - Action ID
     * @returns {Promise} Promise with execution result
     */
    executePotentialAction: (actionId) => nexusApi.post(`/documents/potential-actions/${actionId}/execute/`),

    /**
     * Get action history
     * @param {number|string} actionId - Action ID
     * @returns {Promise} Promise with action history
     */
    getActionHistory: (actionId) => nexusApi.get(`/documents/potential-actions/${actionId}/history/`),

    /**
     * Get potential actions for a document case link
     * @param {number|string} caseLinkId - DocumentCaseLink ID
     * @param {Object} params - Query parameters (status, priority)
     * @returns {Promise} Promise with potential actions list
     */
    getDocumentCaseLinkPotentialActions: (caseLinkId, params = {}) => nexusApi.get(`/documents/case-links/${caseLinkId}/potential-actions/`, { params }),

    /**
     * Get action history for a document
     * @param {number|string} inboundId - Inbound ID
     * @param {number|string} documentId - Document ID
     * @param {Object} params - Query parameters (action_type, success)
     * @returns {Promise} Promise with document actions list
     */
    getDocumentActions: (inboundId, documentId, params = {}) => nexusApi.get(`/documents/inbounds/${inboundId}/document/${documentId}/actions/`, { params }),

    /**
     * Get documents linked to a specific case
     * @param {number} caseId - Case ID
     * @param {Object} params - Query parameters (page, page_size, search)
     * @returns {Promise} Promise with case documents list
     */
    getCaseDocuments: (caseId, params = {}) => nexusApi.get(`/documents/cases/${caseId}/documents/`, { params }),

    /**
     * Get a document by its external ID
     * @param {string} externalId - Document external ID
     * @returns {Promise} Promise with document details
     */
    getDocumentByExternalId: (externalId) => nexusApi.get(`/documents/external/${externalId}/`)
};

// Accounts service
export const accountsService = {
    /**
     * Get all accounts the user has access to
     * @returns {Promise} Promise with accounts list
     */
    getAccountsList: () => nexusApi.get('/accounts/list/'),

    /**
     * Get current account information
     * @returns {Promise} Promise with current account details
     */
    getCurrentAccount: () => nexusApi.get('/accounts/current/'),

    /**
     * Switch to a different account
     * @param {number|string} accountId - Account ID to switch to
     * @returns {Promise} Promise with switch result
     */
    switchAccount: async (accountId) => {
        const response = await nexusApi.post('/accounts/current/', { account_id: accountId });

        // Refresh current account data and admin status after switching
        try {
            const accountResponse = await nexusApi.get('/accounts/current/');
            if (accountResponse.data) {
                // Update stored account info and admin status
                localStorage.setItem('currentAccount', JSON.stringify(accountResponse.data));
                localStorage.setItem('isAccountAdmin', accountResponse.data.is_account_admin ? 'true' : 'false');
            }
        } catch (error) {
            console.error('Failed to refresh account info after switch:', error);
            // Clear admin status on error
            localStorage.setItem('isAccountAdmin', 'false');
        }

        return response;
    },

    /**
     * Get current user's role in the current account
     * @returns {Promise} Promise with role information
     */
    getCurrentUserRole: () => nexusApi.get('/accounts/current/role/'),

    /**
     * Get all accounts
     * @returns {Promise} Promise with accounts list
     */
    getAccounts: () => nexusApi.get('/accounts/'),

    /**
     * Get a specific account
     * @param {number|string} id - Account ID
     * @returns {Promise} Promise with account details
     */
    getAccount: (id) => nexusApi.get(`/accounts/${id}/`),

    /**
     * Create a new account
     * @param {Object} data - Account data
     * @returns {Promise} Promise with created account
     */
    createAccount: (data) => nexusApi.post('/accounts/', data),

    /**
     * Update an account
     * @param {number|string} id - Account ID
     * @param {Object} data - Updated account data
     * @returns {Promise} Promise with updated account
     */
    updateAccount: (id, data) => nexusApi.put(`/accounts/${id}/`, data),

    /**
     * Delete an account
     * @param {number|string} id - Account ID
     * @returns {Promise} Promise with result
     */
    deleteAccount: (id) => nexusApi.delete(`/accounts/${id}/`)
};

// Helper functions for account and admin status
export const authHelpers = {
    /**
     * Check if the current user is an admin
     * @returns {boolean} True if user is admin, false otherwise
     */
    isAccountAdmin: () => {
        return localStorage.getItem('isAccountAdmin') === 'true';
    },

    /**
     * Get current account from localStorage
     * @returns {Object|null} Current account object or null
     */
    getCurrentAccount: () => {
        const account = localStorage.getItem('currentAccount');
        return account ? JSON.parse(account) : null;
    },

    /**
     * Get current user from localStorage
     * @returns {Object|null} Current user object or null
     */
    getCurrentUser: () => {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    },

    /**
     * Refresh the current account from the backend and update the
     * localStorage cache (currentAccount + isAccountAdmin)
     * @returns {Promise<Object|null>} Promise resolving to the fresh account object, or null on failure
     */
    refreshCurrentAccount: async () => {
        try {
            const response = await nexusApi.get('/accounts/current/');
            if (response.data) {
                localStorage.setItem('currentAccount', JSON.stringify(response.data));
                localStorage.setItem('isAccountAdmin', response.data.is_account_admin ? 'true' : 'false');
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Failed to refresh current account:', error);
            return null;
        }
    },

    /**
     * Refresh current account and admin status from backend
     * @returns {Promise<boolean>} Promise resolving to admin status
     */
    refreshAdminStatus: async () => {
        try {
            const response = await nexusApi.get('/accounts/current/');
            if (response.data) {
                const isAdmin = response.data.is_account_admin || false;

                // Update localStorage
                localStorage.setItem('currentAccount', JSON.stringify(response.data));
                localStorage.setItem('isAccountAdmin', isAdmin ? 'true' : 'false');

                return isAdmin;
            }
            return false;
        } catch (error) {
            console.error('Failed to refresh admin status:', error);
            return false;
        }
    }
};

// Mail service for mail providers and email operations
export const mailService = {
    /**
     * Get all mail providers
     * @returns {Promise} Promise with providers list
     */
    getProviders: () => nexusApi.get('/mail/providers/'),

    /**
     * Get a specific mail provider
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with provider details
     */
    getProvider: (id) => nexusApi.get(`/mail/providers/${id}/`),

    /**
     * Create a new mail provider
     * @param {Object} data - Provider data
     * @returns {Promise} Promise with created provider
     */
    createProvider: (data) => nexusApi.post('/mail/providers/', data),

    /**
     * Update a provider
     * @param {number|string} id - Provider ID
     * @param {Object} data - Updated provider data
     * @returns {Promise} Promise with updated provider
     */
    updateProvider: (id, data) => nexusApi.put(`/mail/providers/${id}/`, data),

    /**
     * Partially update a provider
     * @param {number|string} id - Provider ID
     * @param {Object} data - Fields to update
     * @returns {Promise} Promise with updated provider
     */
    patchProvider: (id, data) => nexusApi.patch(`/mail/providers/${id}/`, data),

    /**
     * Delete a provider
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with result
     */
    deleteProvider: (id) => nexusApi.delete(`/mail/providers/${id}/`),

    /**
     * Test provider connection
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with connection test result
     */
    testConnection: (id) => nexusApi.post(`/mail/providers/${id}/test-connection/`),

    /**
     * Fetch emails from a provider
     * @param {Object} data - Fetch parameters (provider_id, limit, since_date)
     * @returns {Promise} Promise with fetch results
     */
    fetchEmails: (data) => nexusApi.post('/mail/fetch/', data),

    /**
     * Get emails
     * @param {Object} params - Query parameters
     * @returns {Promise} Promise with emails list
     */
    getEmails: (params) => nexusApi.get('/mail/emails/', { params }),

    /**
     * Get a specific email
     * @param {number|string} id - Email ID
     * @returns {Promise} Promise with email details
     */
    getEmail: (id) => nexusApi.get(`/mail/emails/${id}/`),

    /**
     * Get email messages with filtering and pagination
     * @param {Object} params - Query parameters (provider_id, sender, recipient, date_from, date_to, limit, offset)
     * @returns {Promise} Promise with messages list
     */
    getMessages: (params) => {
        // Add account_id from current account if not already in params
        const currentAccount = localStorage.getItem('currentAccount');
        if (currentAccount && !params.account_id) {
            try {
                const account = JSON.parse(currentAccount);
                if (account.id) {
                    params = { ...params, account_id: account.id };
                }
            } catch (e) {
                console.error('Error parsing current account:', e);
            }
        }
        return nexusApi.get('/mail/messages/', { params });
    },

    /**
     * Get a single email message by ID
     * @param {number|string} id - Message ID
     * @returns {Promise} Promise with message details
     */
    getMessage: (id) => {
        return nexusApi.get(`/mail/messages/${id}/`);
    },

    /**
     * Get admin email messages with filtering and pagination (admin only)
     * @param {Object} params - Query parameters (auto_fetch, provider_id, sender, recipient, date_from, date_to, limit, offset)
     * @returns {Promise} Promise with messages list
     */
    getAdminMessages: (params) => {
        return nexusApi.get('/mail/messages/admin/', { params });
    },

    /**
     * Get available mailboxes for admin filtering
     * @returns {Promise} Promise with mailboxes list
     */
    getAdminMailboxes: () => {
        return nexusApi.get('/mail/messages/admin/mailboxes/');
    },

    /**
     * Execute email workflow for an email
     * @param {number|string} emailId - Email ID (database ID, not message_id)
     * @param {Object} data - Workflow parameters (workflow_type, arguments)
     * @returns {Promise} Promise with workflow execution result
     */
    executeEmailWorkflow: (emailId, data = {}) => {
        return nexusApi.post(`/mail/emails/${emailId}/execute-workflow/`, data);
    },

    /**
     * Search for cases (manual mail->case linker; same endpoint as the
     * Outlook add-in case picker). Server expects a min-length of 2 chars.
     * @param {string} q - Search query
     * @returns {Promise} Promise with case results
     */
    searchCases: (q) => nexusApi.get('/addin/search-cases/', { params: { q } }),

    /**
     * Link an email message to a case via the addin endpoint.
     * @param {string} internetMessageId - The message's internet_message_id
     * @param {number} caseId - Numeric case id
     * @returns {Promise} Promise resolving to { status: 'linked' | 'already_linked' }
     */
    linkCaseToEmail: (internetMessageId, caseId) => nexusApi.post('/addin/link-case/', { internet_message_id: internetMessageId, case_id: caseId }),

    /**
     * Unlink an email message from a case via the addin endpoint (idempotent).
     * @param {string} internetMessageId - The message's internet_message_id
     * @param {number} caseId - Numeric case id
     * @returns {Promise} Promise resolving on success
     */
    unlinkCaseFromEmail: (internetMessageId, caseId) => nexusApi.post('/addin/unlink-case/', { internet_message_id: internetMessageId, case_id: caseId }),

    /**
     * Attach an email message to one or more cases (manual Needs Match linking).
     * Always returns 200 when the links succeed; attachment re-processing is
     * reported additively under `attachment_processing` and never fails the request.
     *
     * Optional `attachment_decisions` resolve attachments held as possible
     * duplicates (F019): `link_existing` needs a `document_id` among the row's
     * stored candidates; `ingest_new` creates a second document. A decision for
     * a row not on hold is HTTP 400 `error_code: "invalid_attachment_decision"`;
     * a different decision already stored is a per-row `outcome: "error"`,
     * `error_code: "decision_conflict"` (HTTP 200); repeating the stored
     * decision is idempotent.
     *
     * Response `attachment_processing`: legacy `status`, `details[]` (legacy
     * `status`/`reason` plus `outcome`, `attachment_ids`, `error_code`,
     * `document {id, file_name, inbound_id}`, `case_ids`, `match_basis`,
     * `neos_upload`, `candidates [{document_id, file_name, inbound_id, case_id}]`,
     * `decision`), `summary` (count per outcome) and `processing_status`.
     * @param {Object} data - { message_id, case_external_ids, attachment_decisions?: [{attachment_id, decision: 'link_existing'|'ingest_new', document_id?}] }
     * @returns {Promise} Promise resolving with link results + attachment_processing
     */
    attachToCase: (data) => nexusApi.post('/mail/attach-to-case/', data),

    /**
     * Process an email's attachments with optional per-attachment case routing.
     * Posting no `attachment_cases` for a multi-case email returns 200 with
     * `status: "attachment_case_selection_required"` and the selectable
     * attachment ids. `routing_contract: "v2"` opts into the no-fan-out gate.
     * `attachment_decisions` (same shape and errors as `attachToCase`) needs
     * `attachment_cases` routing every decided attachment to at least one case
     * (HTTP 400 `invalid_attachment_decision` otherwise). The response carries
     * `details[]`, `summary` and `processing_status` at the top level.
     * @param {number|string} messageId - Message ID
     * @param {Object} [data] - { attachment_cases?: [{attachment_id, case_ids}], attachment_decisions?: [{attachment_id, decision: 'link_existing'|'ingest_new', document_id?}], routing_contract? }
     * @returns {Promise} Promise resolving with processing result
     */
    processAttachments: (messageId, data = {}) => nexusApi.post(`/mail/messages/${messageId}/process-attachments/`, data),

    /**
     * Resolve held duplicate attachments: a named wrapper over
     * process-attachments (there is no separate decision endpoint).
     * @param {number|string} messageId - Message ID
     * @param {Object} data - { attachment_cases: [{attachment_id, case_ids}], attachment_decisions: [{attachment_id, decision, document_id?}] }
     * @returns {Promise} Promise resolving with the process-attachments result
     */
    resolveAttachmentDecision: (messageId, { attachment_cases, attachment_decisions }) => nexusApi.post(`/mail/messages/${messageId}/process-attachments/`, { routing_contract: 'v2', attachment_cases, attachment_decisions }),

    /**
     * Fetch AI case suggestions for an email's attachments. Never auto-applied;
     * degrades to `{ ai_available: false }` when the prompt is unavailable.
     * @param {number|string} messageId - Message ID
     * @returns {Promise} Promise resolving with { ai_available, attachments }
     */
    suggestAttachmentCases: (messageId) => nexusApi.post(`/mail/messages/${messageId}/suggest-attachment-cases/`),

    /**
     * Get the case-email send configuration for the current account.
     * @returns {Promise} Promise resolving with { enabled, smtp_address, reason }
     */
    getSendConfig: () => nexusApi.get('/mail/send-config/'),

    /**
     * Send a new email from a case mailbox.
     * `__skipRetry` is mandatory: automatic retry of this POST is forbidden;
     * user-driven retries must reuse the same idempotency_key in the FormData.
     * @param {number|string} caseId - Numeric case id
     * @param {FormData} formData - Multipart body: repeated `to`/`cc`/`bcc`, `subject`, `body`, `idempotency_key`, repeated `attachments`
     * @returns {Promise} Promise resolving with a send receipt
     */
    sendCaseEmail: (caseId, formData) => nexusApi.post(`/mail/cases/${caseId}/send-email/`, formData, { __skipRetry: true }),

    /**
     * Reply to an inbound email on a case.
     * `__skipRetry` is mandatory (see sendCaseEmail).
     * @param {number|string} caseId - Numeric case id
     * @param {number|string} emailId - DB id of the selected email being replied to
     * @param {FormData} formData - Multipart body: `body`, `reply_all`, `idempotency_key`, repeated `attachments`
     * @returns {Promise} Promise resolving with a send receipt
     */
    replyCaseEmail: (caseId, emailId, formData) => nexusApi.post(`/mail/cases/${caseId}/emails/${emailId}/reply/`, formData, { __skipRetry: true })
};

// Fax service for fax providers and related endpoints
export const faxService = {
    /**
     * Get all fax providers
     * @returns {Promise} Promise with providers list
     */
    getProviders: () => nexusApi.get('/fax/providers/'),

    /**
     * Get a specific fax provider
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with provider details
     */
    getProvider: (id) => nexusApi.get(`/fax/providers/${id}/`),

    /**
     * Create a new fax provider
     * @param {Object} data - Provider data
     * @returns {Promise} Promise with created provider
     */
    createProvider: (data) => nexusApi.post('/fax/providers/', data),

    /**
     * Update a provider
     * @param {number|string} id - Provider ID
     * @param {Object} data - Updated provider data
     * @returns {Promise} Promise with updated provider
     */
    updateProvider: (id, data) => nexusApi.put(`/fax/providers/${id}/`, data),

    /**
     * Partially update a provider
     * @param {number|string} id - Provider ID
     * @param {Object} data - Fields to update
     * @returns {Promise} Promise with updated provider
     */
    patchProvider: (id, data) => nexusApi.patch(`/fax/providers/${id}/`, data),

    /**
     * Delete a provider
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with result
     */
    deleteProvider: (id) => nexusApi.delete(`/fax/providers/${id}/`),

    /**
     * Test provider connection
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with connection test result
     */
    testConnection: (id) => nexusApi.post(`/fax/providers/${id}/test-connection/`),

    /**
     * Fetch faxes from a provider
     * @param {Object} data - Fetch parameters (provider_id, limit, since_date)
     * @returns {Promise} Promise with fetch results
     */
    fetchFaxes: (data) => nexusApi.post('/fax/fetch/', data),

    /**
     * Get faxes
     * @param {Object} params - Query parameters
     * @returns {Promise} Promise with faxes list
     */
    getFaxes: (params) => nexusApi.get('/fax/faxes/', { params }),

    /**
     * Get a specific fax
     * @param {number|string} id - Fax ID
     * @returns {Promise} Promise with fax details
     */
    getFax: (id) => nexusApi.get(`/fax/faxes/${id}/`)
};

// Scan service for scan providers and related endpoints
export const scanService = {
    /**
     * Get all scan providers
     * @returns {Promise} Promise with providers list
     */
    getProviders: () => nexusApi.get('/scan/providers/'),

    /**
     * Get a specific scan provider
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with provider details
     */
    getProvider: (id) => nexusApi.get(`/scan/providers/${id}/`),

    /**
     * Create a new scan provider
     * @param {Object} data - Provider data
     * @returns {Promise} Promise with created provider
     */
    createProvider: (data) => nexusApi.post('/scan/providers/', data),

    /**
     * Update a provider
     * @param {number|string} id - Provider ID
     * @param {Object} data - Updated provider data
     * @returns {Promise} Promise with updated provider
     */
    updateProvider: (id, data) => nexusApi.put(`/scan/providers/${id}/`, data),

    /**
     * Partially update a provider
     * @param {number|string} id - Provider ID
     * @param {Object} data - Fields to update
     * @returns {Promise} Promise with updated provider
     */
    patchProvider: (id, data) => nexusApi.patch(`/scan/providers/${id}/`, data),

    /**
     * Delete a provider
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with result
     */
    deleteProvider: (id) => nexusApi.delete(`/scan/providers/${id}/`),

    /**
     * Test provider connection
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with connection test result
     */
    testConnection: (id) => nexusApi.post(`/scan/providers/${id}/test/`),

    /**
     * Fetch documents from a provider
     * @param {Object} data - Fetch parameters (provider_id, days_ago, limit, create_inbounds)
     * @returns {Promise} Promise with fetch results
     */
    fetchDocuments: (data) => nexusApi.post(`/scan/providers/${data.provider_id}/fetch/`, data),

    /**
     * Get scanned documents
     * @param {Object} params - Query parameters
     * @returns {Promise} Promise with documents list
     */
    getDocuments: (params) => nexusApi.get('/scan/documents/', { params }),

    /**
     * Get a specific scanned document
     * @param {number|string} id - Document ID
     * @returns {Promise} Promise with document details
     */
    getDocument: (id) => nexusApi.get(`/scan/documents/${id}/`)
};

// Reports service for analytics and statistics
export const reportsService = {
    /**
     * Get daily document statistics
     * @param {Object} params - Query parameters (date_from, date_to)
     * @returns {Promise} Promise with daily statistics data
     */
    getDailyDocumentStats: (params = {}) => nexusApi.get('/reports/documents/daily/', { params })
};

// Notifications service
export const notificationsService = {
    /**
     * Get all notifications for the current user
     * @param {Object} params - Query parameters (page, page_size, etc.)
     * @returns {Promise} Promise with notifications list
     */
    getNotifications: (params = {}) => nexusApi.get('/notifications/notifications/', { params }),

    getSourceCounts: (params = {}) => nexusApi.get('/notifications/notifications/source_counts/', { params }),

    /**
     * Get unread notifications for the current user
     * @returns {Promise} Promise with unread notifications
     */
    getUnreadNotifications: () => nexusApi.get('/notifications/notifications/unread/'),

    /**
     * Mark a notification as read
     * @param {number|string} id - Notification ID
     * @returns {Promise} Promise with result
     */
    markAsRead: (id) => nexusApi.post(`/notifications/notifications/${id}/mark_read/`),

    /**
     * Mark all notifications as read for the current user
     * @returns {Promise} Promise with result
     */
    markAllAsRead: (data = {}) => nexusApi.post('/notifications/notifications/mark_all_read/', data),

    // Notification Rules (Admin only)
    /**
     * Get all notification rules for the account
     * @param {Object} params - Query parameters
     * @returns {Promise} Promise with rules list
     */
    getRules: (params = {}) => nexusApi.get('/notifications/rules/', { params }),

    /**
     * Get a specific notification rule
     * @param {number|string} id - Rule ID
     * @returns {Promise} Promise with rule details
     */
    getRule: (id) => nexusApi.get(`/notifications/rules/${id}/`),

    /**
     * Create a new notification rule
     * @param {Object} data - Rule data
     * @returns {Promise} Promise with created rule
     */
    createRule: (data) => nexusApi.post('/notifications/rules/', data),

    /**
     * Update a notification rule
     * @param {number|string} id - Rule ID
     * @param {Object} data - Updated rule data
     * @returns {Promise} Promise with updated rule
     */
    updateRule: (id, data) => nexusApi.put(`/notifications/rules/${id}/`, data),

    /**
     * Delete a notification rule
     * @param {number|string} id - Rule ID
     * @returns {Promise} Promise with result
     */
    deleteRule: (id) => nexusApi.delete(`/notifications/rules/${id}/`),

    /**
     * Get available trigger events
     * @returns {Promise} Promise with trigger events
     */
    getTriggerEvents: () => nexusApi.get('/notifications/rules/trigger_events/'),

    /**
     * Get document categories with hierarchy
     * @returns {Promise} Promise with categories
     */
    getCategories: () => nexusApi.get('/notifications/rules/categories/'),

    /**
     * Get available staff roles
     * @returns {Promise} Promise with staff roles
     */
    getStaffRoles: () => nexusApi.get('/notifications/rules/staff_roles/'),

    // Notification Templates (Admin only)
    /**
     * Get all notification templates
     * @param {Object} params - Query parameters
     * @returns {Promise} Promise with templates list
     */
    getTemplates: (params = {}) => nexusApi.get('/notifications/templates/', { params }),

    /**
     * Get a specific notification template
     * @param {number|string} id - Template ID
     * @returns {Promise} Promise with template details
     */
    getTemplate: (id) => nexusApi.get(`/notifications/templates/${id}/`),

    /**
     * Create a new notification template
     * @param {Object} data - Template data
     * @returns {Promise} Promise with created template
     */
    createTemplate: (data) => nexusApi.post('/notifications/templates/', data),

    /**
     * Update a notification template
     * @param {number|string} id - Template ID
     * @param {Object} data - Updated template data
     * @returns {Promise} Promise with updated template
     */
    updateTemplate: (id, data) => nexusApi.put(`/notifications/templates/${id}/`, data),

    /**
     * Delete a notification template
     * @param {number|string} id - Template ID
     * @returns {Promise} Promise with result
     */
    deleteTemplate: (id) => nexusApi.delete(`/notifications/templates/${id}/`),

    // Notification Preferences
    /**
     * Get all notification preferences (admin sees all, users see their own)
     * @param {Object} params - Query parameters
     * @returns {Promise} Promise with preferences list
     */
    getPreferences: (params = {}) => nexusApi.get('/notifications/preferences/', { params }),

    /**
     * Get current user's preferences
     * @returns {Promise} Promise with user preferences
     */
    getMyPreferences: () => nexusApi.get('/notifications/preferences/my_preferences/'),

    /**
     * Update current user's preferences
     * @param {Object} data - Updated preferences
     * @returns {Promise} Promise with updated preferences
     */
    updateMyPreferences: (data) => nexusApi.patch('/notifications/preferences/my_preferences/', data),

    /**
     * Get a specific user's preferences (admin only)
     * @param {number|string} id - Preference ID
     * @returns {Promise} Promise with preference details
     */
    getPreference: (id) => nexusApi.get(`/notifications/preferences/${id}/`),

    /**
     * Update a user's preferences (admin only)
     * @param {number|string} id - Preference ID
     * @param {Object} data - Updated preferences
     * @returns {Promise} Promise with updated preferences
     */
    updatePreference: (id, data) => nexusApi.put(`/notifications/preferences/${id}/`, data),

    // Admin Notifications Service
    adminNotifications: {
        /**
         * Get all notifications (admin only)
         * @param {Object} params - Query parameters (page, page_size, filters)
         * @returns {Promise} Promise with notifications list
         */
        list: (params = {}) => nexusApi.get('/notifications/admin-notifications/', { params }),

        /**
         * Get available event types
         * @returns {Promise} Promise with event types
         */
        eventTypes: () => nexusApi.get('/notifications/admin-notifications/event_types/'),

        /**
         * Get delivery statuses
         * @returns {Promise} Promise with delivery statuses
         */
        deliveryStatuses: () => nexusApi.get('/notifications/admin-notifications/delivery_statuses/'),

        /**
         * Get priorities
         * @returns {Promise} Promise with priorities
         */
        priorities: () => nexusApi.get('/notifications/admin-notifications/priorities/'),

        /**
         * Get users for filtering/selection
         * @param {string} search - Optional search query
         * @returns {Promise} Promise with users list
         */
        users: (search = '') => nexusApi.get('/notifications/admin-notifications/users/', search ? { params: { search } } : {}),

        /**
         * Get all possible recipients (users + staff) for filtering/selection
         * @param {string} search - Optional search query
         * @returns {Promise} Promise with recipients list
         */
        recipients: (search = '') => nexusApi.get('/notifications/admin-notifications/recipients/', search ? { params: { search } } : {}),

        /**
         * Send a custom notification
         * @param {Object} data - Notification data (recipient_ids, title, message, etc.)
         * @returns {Promise} Promise with send result
         */
        send: (data) => nexusApi.post('/notifications/admin-notifications/send/', data),

        /**
         * Resend a specific notification
         * @param {number|string} id - Notification ID
         * @param {Object} data - Optional data including force parameter
         * @returns {Promise} Promise with resend result
         */
        resend: (id, data = {}) => nexusApi.post(`/notifications/admin-notifications/${id}/resend/`, data),

        /**
         * Get notification statistics
         * @param {Object} params - Query parameters (date_from, date_to)
         * @returns {Promise} Promise with statistics
         */
        statistics: (params = {}) => nexusApi.get('/notifications/admin-notifications/statistics/', { params }),

        /**
         * Check for missed notifications (admin only)
         * @param {Object} data - Request data (days_back, status, send_notifications, page, page_size)
         * @returns {Promise} Promise with missed notifications and pagination info
         */
        checkMissed: (data = {}) => nexusApi.post('/documents/check-missed-notifications/', data),

        /**
         * Resend notification for a specific DocumentCaseLink
         * @param {number|string} caseLinkId - DocumentCaseLink ID
         * @returns {Promise} Promise with resend result
         */
        resendCaseLinkNotification: (caseLinkId) => nexusApi.post(`/documents/case-links/${caseLinkId}/resend-notification/`)
    }
};

// AI service
export const aiService = {
    // AI Providers
    /**
     * Get all AI providers for the account
     * @param {Object} params - Query parameters (status, type, page, page_size)
     * @returns {Promise} Promise with providers list
     */
    getProviders: (params = {}) => nexusApi.get('/ai/providers/', { params }),

    /**
     * Get a specific AI provider
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with provider details
     */
    getProvider: (id) => nexusApi.get(`/ai/providers/${id}/`),

    /**
     * Create a new AI provider
     * @param {Object} data - Provider data
     * @returns {Promise} Promise with created provider
     */
    createProvider: (data) => nexusApi.post('/ai/providers/', data),

    /**
     * Update an AI provider
     * @param {number|string} id - Provider ID
     * @param {Object} data - Provider data
     * @returns {Promise} Promise with updated provider
     */
    updateProvider: (id, data) => nexusApi.patch(`/ai/providers/${id}/`, data),

    /**
     * Delete an AI provider
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with deletion result
     */
    deleteProvider: (id) => nexusApi.delete(`/ai/providers/${id}/`),

    /**
     * Test AI provider connection
     * @param {number|string} id - Provider ID
     * @returns {Promise} Promise with test result
     */
    testProviderConnection: (id) => nexusApi.post(`/ai/providers/${id}/test_connection/`),

    /**
     * Get available provider types
     * @returns {Promise} Promise with provider types
     */
    getProviderTypes: () => nexusApi.get('/ai/providers/provider_types/'),

    // Prompts
    /**
     * Get all prompts
     * @param {Object} params - Query parameters (prompt_type, ai_provider, category, search, page, page_size)
     * @returns {Promise} Promise with prompts list
     */
    getPrompts: (params = {}) => nexusApi.get('/ai/prompts/', { params }),

    /**
     * Get a specific prompt
     * @param {number|string} id - Prompt ID
     * @returns {Promise} Promise with prompt details
     */
    getPrompt: (id) => nexusApi.get(`/ai/prompts/${id}/`),

    /**
     * Create a new prompt
     * @param {Object} data - Prompt data
     * @returns {Promise} Promise with created prompt
     */
    createPrompt: (data) => nexusApi.post('/ai/prompts/', data),

    /**
     * Update a prompt
     * @param {number|string} id - Prompt ID
     * @param {Object} data - Prompt data
     * @returns {Promise} Promise with updated prompt
     */
    updatePrompt: (id, data) => nexusApi.patch(`/ai/prompts/${id}/`, data),

    /**
     * Delete a prompt
     * @param {number|string} id - Prompt ID
     * @returns {Promise} Promise with deletion result
     */
    deletePrompt: (id) => nexusApi.delete(`/ai/prompts/${id}/`),

    /**
     * Bulk create prompts
     * @param {Object} data - Object with prompts array
     * @returns {Promise} Promise with bulk creation result
     */
    bulkCreatePrompts: (data) => nexusApi.post('/ai/prompts/bulk_create/', data),

    /**
     * Get available prompt types
     * @returns {Promise} Promise with prompt types
     */
    getPromptTypes: () => nexusApi.get('/ai/prompts/prompt_types/'),

    // Prompt Results (Read-only)
    /**
     * Get prompt results
     * @param {Object} params - Query parameters (status, prompt_type, ai_provider, document, private, date_from, date_to, search, page, page_size)
     * @returns {Promise} Promise with results list
     */
    getPromptResults: (params = {}) => nexusApi.get('/ai/prompt-results/', { params }),

    /**
     * Get a specific prompt result
     * @param {number|string} id - Result ID
     * @returns {Promise} Promise with result details
     */
    getPromptResult: (id) => nexusApi.get(`/ai/prompt-results/${id}/`),

    /**
     * Score a prompt result with user feedback
     * @param {number|string} id - Result ID
     * @param {Object} data - Score data (user_rating, feedback_reason)
     * @returns {Promise} Promise with updated result
     */
    scorePromptResult: (id, data) => nexusApi.post(`/ai/prompt-results/${id}/score/`, data),

    /**
     * Get prompt results statistics
     * @param {Object} params - Query parameters (date_from, date_to)
     * @returns {Promise} Promise with statistics
     */
    getPromptResultsStatistics: (params = {}) => nexusApi.get('/ai/prompt-results/statistics/', { params }),

    /**
     * Submit a document for batch AI processing
     * @param {Object} data - Batch submission data
     * @param {number} data.inbound_id - Inbound ID
     * @param {number} data.document_id - Document ID
     * @param {string} data.prompt_type - Prompt type to execute
     * @param {string} data.priority - Priority level (low, medium, high)
     * @returns {Promise} Promise with batch submission result
     */
    submitBatch: (data) => nexusApi.post('/ai/batch/submit/', data),

    /**
     * Check batch processing status
     * @param {string} batchId - Batch ID
     * @param {Object} params - Query parameters (inbound_id)
     * @returns {Promise} Promise with batch status
     */
    getBatchStatus: (batchId, params = {}) => nexusApi.get(`/ai/batch/${batchId}/status/`, { params }),

    // Replay Runs
    createReplayRun: (data) => nexusApi.post('/ai/replay-runs/', data),
    getReplayRuns: (params = {}) => nexusApi.get('/ai/replay-runs/', { params }),
    getReplayRun: (id) => nexusApi.get(`/ai/replay-runs/${id}/`),
    getReplayRunResults: (id, params = {}) => nexusApi.get(`/ai/replay-runs/${id}/results/`, { params }),
    previewReplayRun: (data) => nexusApi.post('/ai/replay-runs/preview/', data),
    analyzeReplayRun: (id) => nexusApi.post(`/ai/replay-runs/${id}/analyze/`),
    getOpenRouterModels: () => nexusApi.get('/ai/openrouter-models/')
};

// PDF Forms service for document generation
const pdfFormsService = {
    /**
     * Get available forms for a case
     * @param {string} caseNumber - Case number
     * @returns {Promise} Promise with available form templates
     */
    getAvailableForms: (caseNumber) => nexusApi.get(`/pdf_forms/forms/cases/${caseNumber}/available/`),

    /**
     * Start form generation process
     * @param {string} caseNumber - Case number
     * @param {Object} data - Generation data with template_id
     * @returns {Promise} Promise with form generation object
     */
    generateForm: (caseNumber, data) => nexusApi.post(`/pdf_forms/forms/cases/${caseNumber}/generate/`, data),

    /**
     * Update form data
     * @param {number} formId - Form generation ID
     * @param {Object} data - Updated form data
     * @returns {Promise} Promise with update result
     */
    updateFormData: (formId, data) => nexusApi.put(`/pdf_forms/forms/${formId}/edit/`, data),

    /**
     * Get form template schema
     * @param {number} formId - Form generation ID
     * @returns {Promise} Promise with schema and current data
     */
    getFormSchema: (formId) => nexusApi.get(`/pdf_forms/forms/${formId}/schema/`),

    /**
     * Submit form for PDF generation
     * @param {number} formId - Form generation ID
     * @returns {Promise} Promise with submission result
     */
    submitForm: (formId) => nexusApi.post(`/pdf_forms/forms/${formId}/submit/`),

    /**
     * Get form generation history for a case
     * @param {string} caseNumber - Case number
     * @returns {Promise} Promise with form generation history
     */
    getFormHistory: (caseNumber) => nexusApi.get(`/pdf_forms/forms/cases/${caseNumber}/history/`),

    /**
     * Get form generation data for review
     * @param {string} callbackToken - Callback token
     * @returns {Promise} Promise with form generation data
     */
    getFormGenerationForReview: (callbackToken) => nexusApi.get(`/pdf_forms/workflow/review/${callbackToken}/`),

    /**
     * Resume workflow callback for user review
     * @param {string} callbackToken - Callback token
     * @param {Object} data - Edited form data
     * @returns {Promise} Promise with callback result
     */
    resumeWorkflow: (callbackToken, data) => nexusApi.post(`/pdf_forms/workflow/callback/${callbackToken}/`, data)
};

// Call tracking service for CTM calls and text conversations
export const callTrackingService = {
    getCaseCalls: (accountId, caseId, params = {}) => nexusApi.get(`/call-tracking/accounts/${accountId}/cases/${caseId}/calls/`, { params }),

    getCaseTexts: (accountId, caseId, params = {}) => nexusApi.get(`/call-tracking/accounts/${accountId}/cases/${caseId}/texts/`, { params }),

    getConversationMessages: (conversationId) => nexusApi.get(`/call-tracking/conversations/${conversationId}/messages/`),

    getCallDetail: (callId) => nexusApi.get(`/call-tracking/calls/${callId}/`),

    getCallAudioUrl: (callId) => nexusApi.get(`/call-tracking/calls/${callId}/audio-url/`),

    getAdminCommunications: (params = {}) => nexusApi.get('/call-tracking/admin/communications/', { params }),

    getUnmatched: (accountId) => nexusApi.get(`/call-tracking/accounts/${accountId}/unmatched/`),

    linkCallCases: (callId, caseIds) => nexusApi.post(`/call-tracking/calls/${callId}/link-cases/`, { case_ids: caseIds }),

    linkConversationCases: (conversationId, caseIds) => nexusApi.post(`/call-tracking/conversations/${conversationId}/link-cases/`, { case_ids: caseIds }),

    triggerCallWorkflow: (callId) => nexusApi.post(`/call-tracking/calls/${callId}/execute-workflow/`),

    triggerTextWorkflow: (conversationId) => nexusApi.post(`/call-tracking/conversations/${conversationId}/execute-workflow/`),

    getCaseTimeline: (accountId, caseId, params = {}) => nexusApi.get(`/call-tracking/accounts/${accountId}/cases/${caseId}/timeline/`, { params }),

    unlinkCallCase: (callId, caseId) => nexusApi.post(`/call-tracking/calls/${callId}/unlink-case/`, { case_id: caseId }),

    unlinkConversationCase: (conversationId, caseId) => nexusApi.post(`/call-tracking/conversations/${conversationId}/unlink-case/`, { case_id: caseId }),

    getAccountCommunications: (accountId, params = {}) => nexusApi.get(`/call-tracking/accounts/${accountId}/communications/`, { params }),

    sendMessage: (conversationId, data) => nexusApi.post(`/call-tracking/conversations/${conversationId}/send-message/`, data),

    startText: (accountId, caseId, data) => nexusApi.post(`/call-tracking/accounts/${accountId}/cases/${caseId}/start-text/`, data),

    getTextingNumbers: (accountId, caseId) => nexusApi.get(`/call-tracking/accounts/${accountId}/cases/${caseId}/texting-numbers/`),

    getTextableContacts: (accountId, caseId) => nexusApi.get(`/call-tracking/accounts/${accountId}/cases/${caseId}/textable-contacts/`),

    createTextableContact: (accountId, caseId, data) => nexusApi.post(`/call-tracking/accounts/${accountId}/cases/${caseId}/textable-contacts/`, data),

    /** Get the account's click-to-call configuration. */
    getCallConfig: (accountId) => nexusApi.get(`/call-tracking/accounts/${accountId}/call-config/`),

    /**
     * Zoom texting consent (member's own Zoom OAuth grant). Account-scoped by the X-Account-ID
     * header the request interceptor attaches (callers may pass their account id; it is not used).
     * Start → `{url}` (403 `no_zoom_user`, 503 `oauth_not_configured`); status → `{linked,
     * connected, reconnect_required, granted_at, number_masked}`.
     */
    zoomOauthStart: () => nexusApi.get(`/call-tracking/zoom/oauth/start/`),

    zoomOauthStatus: () => nexusApi.get(`/call-tracking/zoom/oauth/status/`),

    /** Account-scoped contact search for the dialer page (ADR 0069): `{config, mode, query, contacts}`. */
    callableSearch: (accountId, params = {}) => nexusApi.get(`/call-tracking/accounts/${accountId}/callable-search/`, { params }),

    /** Get the case contacts and phones that can be called. */
    getCallableContacts: (accountId, caseId) => nexusApi.get(`/call-tracking/accounts/${accountId}/cases/${caseId}/callable-contacts/`),

    /**
     * Start a click-to-call attempt for a case contact's phone.
     * `__skipRetry` is mandatory: a retried POST would place a second call.
     * @param {number|string} accountId - Call-tracking account ID
     * @param {number|string} caseId - Case ID
     * @param {{case_contact_id: number, phone_id: number}} data - Contact and phone to call
     * @returns {Promise} Promise with the call attempt
     */
    startCall: (accountId, caseId, data) => nexusApi.post(`/call-tracking/accounts/${accountId}/cases/${caseId}/start-call/`, data, { __skipRetry: true }),

    /** Get one click-to-call attempt (status polling). */
    getCallAttempt: (accountId, caseId, attemptId) => nexusApi.get(`/call-tracking/accounts/${accountId}/cases/${caseId}/calls/${attemptId}/`),

    /**
     * Hang up a click-to-call attempt.
     * `__skipRetry` is mandatory (see startCall).
     */
    hangUpCall: (accountId, caseId, attemptId) => nexusApi.post(`/call-tracking/accounts/${accountId}/cases/${caseId}/calls/${attemptId}/hang-up/`, undefined, { __skipRetry: true }),

    /**
     * Ad-hoc call to a typed number or a lead: `{number}` or `{lead_id}`, optional `case_id`.
     * `__skipRetry` is mandatory (see startCall).
     */
    startNumberCall: (accountId, data) => nexusApi.post(`/call-tracking/accounts/${accountId}/start-call/`, data, { __skipRetry: true }),

    /** Get any click-to-call attempt of the account, with or without a case (status polling). */
    getCallAttemptById: (accountId, attemptId) => nexusApi.get(`/call-tracking/accounts/${accountId}/call-attempts/${attemptId}/`),

    /**
     * Hang up any click-to-call attempt of the account, with or without a case.
     * `__skipRetry` is mandatory (see startCall).
     */
    hangUpCallById: (accountId, attemptId) => nexusApi.post(`/call-tracking/accounts/${accountId}/call-attempts/${attemptId}/hang-up/`, undefined, { __skipRetry: true }),

    /** Account cases to attach to an ad-hoc call: `{cases: [{case_id, case_number, name, status, raw_status, is_mine}]}`. */
    casePicker: (accountId, params = {}) => nexusApi.get(`/call-tracking/accounts/${accountId}/case-picker/`, { params })
};

const ragService = {
    conversations: (caseId) => nexusApi.get(`/ai/rag/conversations/${caseId}/`),
    messages: (conversationId) => nexusApi.get(`/ai/rag/conversations/${conversationId}/messages/`)
};

const generatedDocumentService = {
    get: (id) => nexusApi.get(`/ai/rag/generated-documents/${id}/`),
    download: (id) => nexusApi.get(`/ai/rag/generated-documents/${id}/download/`, { responseType: 'blob' }),
    attach: (id) => nexusApi.post(`/ai/rag/generated-documents/${id}/attach/`),
    email: (id, payload) => nexusApi.post(`/ai/rag/generated-documents/${id}/email/`, payload)
};

let _remediationMatrixPromise = null;

const medicalChronologyService = {
    get: (caseId) => nexusApi.get(`/medical-chronology/${caseId}/`),
    exportDocx: (caseId) => nexusApi.get(`/medical-chronology/${caseId}/export/`, { responseType: 'blob' }),
    regenerateQuality: (caseId, { forceRun = false, forceFull = false } = {}) => nexusApi.post(`/medical-chronology/${caseId}/regenerate-quality/`, { force_run: forceRun, force_full: forceFull }),
    listRuns: (caseId, { page } = {}) => nexusApi.get(`/medical-chronology/${caseId}/runs/`, { params: page ? { page } : undefined }),
    getRun: (runId) => nexusApi.get(`/medical-chronology/runs/${runId}/`),
    getRunManifest: (runId) => nexusApi.get(`/medical-chronology/runs/${runId}/manifest/`),
    listReviewItems: (runId, { status, severity, issueType } = {}) => {
        const params = {};
        if (status) params.status = status;
        if (severity) params.severity = severity;
        if (issueType) params.issue_type = issueType;
        return nexusApi.get(`/medical-chronology/runs/${runId}/review-items/`, { params });
    },
    getReviewItem: (itemId) => nexusApi.get(`/medical-chronology/review-items/${itemId}/`),
    resolveReviewItem: (itemId, { resolution, candidateId, value, notes } = {}) => {
        const body = { resolution };
        if (candidateId !== undefined) body.candidate_id = candidateId;
        if (value !== undefined) body.value = value;
        if (notes !== undefined) body.notes = notes;
        return nexusApi.post(`/medical-chronology/review-items/${itemId}/resolve/`, body);
    },
    dismissReviewItem: (itemId, { dismissReason } = {}) => nexusApi.post(`/medical-chronology/review-items/${itemId}/dismiss/`, { dismiss_reason: dismissReason }),
    rerunOcr: (runId, { documentId }) => nexusApi.post(`/medical-chronology/runs/${runId}/rerun-ocr/`, { document_id: documentId }),
    rerunSlicing: (runId, { documentId }) => nexusApi.post(`/medical-chronology/runs/${runId}/rerun-slicing/`, { document_id: documentId }),
    rerunExtraction: (runId, { sliceId }) => nexusApi.post(`/medical-chronology/runs/${runId}/rerun-extraction/`, { slice_id: sliceId }),
    reassemble: (runId) => nexusApi.post(`/medical-chronology/runs/${runId}/reassemble/`),
    getDownloadCleanFolder: (runId) => nexusApi.get(`/medical-chronology/runs/${runId}/download-clean-folder/`, { responseType: 'blob' }),
    getCleanRecords: (caseId) => nexusApi.get(`/medical-chronology/${caseId}/clean-records/`),
    getCleanRecordSlicePdf: (sliceId) => nexusApi.get(`/medical-chronology/clean-records/slices/${sliceId}/pdf/`),
    exportCleanRecords: (caseId) => nexusApi.get(`/medical-chronology/${caseId}/clean-records/export/`),
    getReviewItemRemediationMatrix: () => {
        if (!_remediationMatrixPromise) {
            _remediationMatrixPromise = nexusApi.get('/medical-chronology/review-item-remediation-matrix/').catch((err) => {
                _remediationMatrixPromise = null;
                throw err;
            });
        }
        return _remediationMatrixPromise;
    },
    _resetRemediationMatrixCache: () => {
        _remediationMatrixPromise = null;
    }
};

const medicalEvidenceService = {
    get: (caseId) => nexusApi.get(`/medical-evidence/${caseId}/`),
    // Account-wide, not case-scoped: cases older than N days holding one or
    // more providers with no subpoena on file. Params are passed straight
    // through — `older_than_days`, `limit`, `offset`, `crm_provider_id`,
    // `include_ungated`, `as_of` — because the backend owns every default and
    // restating them here is how the screen and the CSV start disagreeing.
    missingSubpoenasReport: (params = {}) => nexusApi.get('/medical-evidence/reports/missing-subpoenas/', { params }),
    createProvider: (caseId, payload) => nexusApi.post(`/medical-evidence/${caseId}/providers/`, payload),
    updateProvider: (caseId, providerId, payload) => nexusApi.patch(`/medical-evidence/${caseId}/providers/${providerId}/`, payload),
    confirmProvider: (caseId, providerId) => nexusApi.patch(`/medical-evidence/${caseId}/providers/${providerId}/`, { confirmed: true }),
    mergeProvider: (caseId, providerId, { intoProviderId }) => nexusApi.post(`/medical-evidence/${caseId}/providers/${providerId}/merge/`, { into_provider_id: intoProviderId }),
    archiveProvider: (caseId, providerId, { reason }) => nexusApi.post(`/medical-evidence/${caseId}/providers/${providerId}/archive/`, { reason }),
    updateRequestState: (caseId, providerId, payload) => nexusApi.patch(`/medical-evidence/${caseId}/providers/${providerId}/request-state/`, payload),
    // Deliberately NOT the payload's `unlinked_documents` tray: that tray is
    // medical-category-only, which makes a subpoena filed under Legal
    // unreachable rather than merely un-analysed. Search is server-side via `q`
    // and the page is capped at 200, so never filter the returned page
    // client-side — the row you want may simply be on the next page.
    attachableDocuments: (caseId, { q, category, limit, offset } = {}) => {
        const params = {};
        if (q) params.q = q;
        if (category !== undefined && category !== null) params.category = category;
        if (limit !== undefined) params.limit = limit;
        if (offset !== undefined) params.offset = offset;
        return nexusApi.get(`/medical-evidence/${caseId}/attachable-documents/`, { params });
    },
    // Responds with the FULL medical-evidence payload, not the created row, so
    // callers replace state rather than splicing. A manual write by a signed-in
    // user is born `human_verified` — that verification, not the document link,
    // is what turns the provider's light from red to yellow.
    createRecordsRequest: (caseId, providerId, payload) => nexusApi.post(`/medical-evidence/${caseId}/providers/${providerId}/records-requests/`, payload),
    // Provider-scoped WC-508 generation. The server resolves the case's fixed
    // assigned attorney, provider values, template and signature; the browser
    // supplies no editable legal data or durable asset URLs. Callers retain one
    // idempotency key across retries of the same attempt.
    generateProviderSubpoena: (caseId, providerId, payload) => nexusApi.post(`/medical-evidence/${caseId}/providers/${providerId}/subpoena-generations/`, payload),
    providerSubpoenaDownload: (documentId) => nexusApi.get(`/documents/documents/${documentId}/pdf/`),
    // Files a records request from a document that is NOT on the case yet: the
    // caller puts the bytes in GCS itself (directUpload with
    // `deferConfirmation`) and hands over the resulting `upload_id`.
    //
    // The server then does confirm-upload → case link → provider link → records
    // request in ONE transaction, and answers with the full medical-evidence
    // payload like every other mutation. So there is nothing here for the client
    // to orchestrate and nothing to poll: four steps, one call, one refetch.
    //
    // `idempotency_key` is the guard against a replayed upload filing a SECOND
    // records request that then supersedes the first — fabricating a served
    // instrument history nobody produced. Callers must reuse one key across
    // every retry of the same attempt.
    uploadSubpoena: (caseId, providerId, payload) => nexusApi.post(`/medical-evidence/${caseId}/providers/${providerId}/upload-subpoena/`, payload),
    // Attaches a document to a provider as one of its MEDICAL RECORDS — the
    // sibling of `uploadSubpoena`, and the intake path email cannot serve:
    // defence counsel and adjusters share productions over SharePoint, OneDrive
    // and Citrix, and a 40 MB production cannot be forwarded past a 25 MB
    // attachment cap. See nexus ADR 0035.
    //
    // The body accepts EXACTLY ONE of `upload_id` (a fresh upload, whose bytes
    // the caller put in GCS with `deferConfirmation`) or `document_id` (the
    // duplicate branch: `request-upload-url/` answers 409 BEFORE minting an
    // upload id, so on that branch there is nothing to confirm and the existing
    // document is what the caller holds), plus an optional `idempotency_key`.
    // No other field — no dates, no citations, no page spans; those are
    // transitions on an existing link and `document-links/<id>/` owns them.
    //
    // Confirm → case link → provider link happen in ONE server-side transaction
    // and the response is the FULL medical-evidence payload, for success, for
    // both duplicate branches and for every idempotent replay. So there is
    // nothing here to orchestrate and nothing to poll: apply the body.
    //
    // The link it writes is `medical_record` and it is attributed to the signed-
    // in user, which is what takes the provider GREEN — and under ADR 0033 green
    // is what starts automated record discovery. This is not a cosmetic write.
    uploadRecord: (caseId, providerId, payload) => nexusApi.post(`/medical-evidence/${caseId}/providers/${providerId}/upload-record/`, payload),
    // Files the records requests carried by ONE document for SEVERAL providers,
    // in one server-side transaction — the bundle case, where every subpoena on
    // a case is served and scanned as a single PDF.
    //
    // Body: `{document_id, assignments: [{provider_id, page_ranges?, ...instrument}]}`.
    // The instrument facts repeat on each assignment because the server files
    // each one independently; serving the bundle was a single act, so the client
    // states them once and fans them out here rather than inventing a shared
    // "filing" the ledger has no room for.
    //
    // All or nothing on purpose. The alternative — the client looping
    // `createRecordsRequest` per provider — leaves a half-split bundle behind on
    // any failure, and a half-split bundle is worse than an unsplit one: some
    // lights have moved and nothing records which pages are still unclaimed.
    splitRecordsRequests: (caseId, payload) => nexusApi.post(`/medical-evidence/${caseId}/records-requests/split/`, payload),
    // The sibling of `splitRecordsRequests` for the OTHER split mode: one
    // combined production of the records themselves, filed as `medical_record`
    // links (ranged spans or whole-document) for several providers in one
    // all-or-nothing server-side transaction.
    //
    // Body: `{document_id, assignments: [{provider_id, page_ranges?}]}` — NO
    // instrument fields; a medical record is not a served instrument. A 409
    // names the conflicting assignment (e.g. the provider already holds a
    // records-request link over the whole document) and nothing is written.
    splitDocumentLinks: (caseId, payload) => nexusApi.post(`/medical-evidence/${caseId}/document-links/split/`, payload),
    /**
     * Ask LFI to propose per-provider page ranges for ONE bundle before it is
     * split. Read-only: the server writes nothing durable, and every proposed
     * span must still be explicitly approved and filed by a human through
     * `splitRecordsRequests`. Never auto-applied; degrades to
     * `{ ai_available: false }` when the prompt, provider or model output is
     * unavailable rather than erroring.
     * @param {number|string} caseId - Case ID
     * @param {number|string} documentId - Document ID of the bundle to analyze
     * @returns {Promise} Promise resolving with { ai_available, provider_page_ranges, unmatched_targets, general_info, provider_general_info, ... }.
     *     `general_info` is document-level instrument facts (dates ISO, enum
     *     values from the server's own vocabulary, per-field {page, quote}
     *     citation + verified flag); fields are omitted when the document does
     *     not state them. `provider_general_info` is the per-provider version
     *     of the same shape, keyed by provider id and restricted to providers
     *     present in the validated provider_page_ranges — a field the bundle
     *     states inconsistently lands here per provider instead of vanishing.
     *     The key is absent from older backends; treat that as a no-op.
     *     Prefill only verified fields into untouched inputs — never
     *     auto-applied.
     */
    suggestSplitPageRanges: (caseId, documentId) => nexusApi.post(`/medical-evidence/${caseId}/documents/${documentId}/suggest-page-ranges/`),
    suggestServiceDates: (caseId, documentId, providerId) => nexusApi.post(`/medical-evidence/${caseId}/documents/${documentId}/providers/${providerId}/suggest-service-dates/`),
    /**
     * Adopt a custodian that `suggestSplitPageRanges` reported in
     * `unmatched_targets` (named on the bundle, absent from the ledger) as a
     * provider on the case. A human clicked "Add as provider" and reviewed the
     * fields, so the provider is created `user_added` + confirmed; the
     * server-side audit row additionally records where it was discovered
     * (document, page, quote). Filing the pages is NOT part of this call — the
     * split submit still does that. Deduped server-side by the same rule that
     * produced the unmatched target: a name already on the ledger returns the
     * existing provider with `adopted.created === false` instead of a twin.
     * @param {number|string} caseId - Case ID
     * @param {number|string} documentId - Document ID of the bundle naming the custodian
     * @param {Object} payload - Provider fields (same as `createProvider`) plus the REQUIRED
     *     `citation: {page, quote}` from the unmatched target.
     * @returns {Promise} Promise resolving with the full tab payload plus
     *     `adopted: {provider_id, created, facility_name}`.
     */
    adoptCustodian: (caseId, documentId, payload) => nexusApi.post(`/medical-evidence/${caseId}/documents/${documentId}/adopt-custodian/`, payload),
    // `document_id` is NOT patchable: swapping which document the instrument
    // lives on means delete + re-create, never a PATCH.
    updateRecordsRequest: (caseId, requestId, payload) => nexusApi.patch(`/medical-evidence/${caseId}/records-requests/${requestId}/`, payload),
    // Soft-remove — what was served and later retracted is history, not noise.
    // It does NOT detach the document link, so a caller that wants the document
    // gone from the provider must also call `deleteDocumentLink`.
    deleteRecordsRequest: (caseId, requestId) => nexusApi.delete(`/medical-evidence/${caseId}/records-requests/${requestId}/`),
    createDocumentLink: (caseId, payload) => nexusApi.post(`/medical-evidence/${caseId}/document-links/`, payload),
    updateDocumentLink: (caseId, linkId, payload) => nexusApi.patch(`/medical-evidence/${caseId}/document-links/${linkId}/`, payload),
    deleteDocumentLink: (caseId, linkId) => nexusApi.delete(`/medical-evidence/${caseId}/document-links/${linkId}/`),
    // Page spans live on their own routes, never as fields on the link PATCH:
    // what a link claims can only change through an audited transition.
    // `attachPageRanges` EXTENDS a link already scoped to ranges (one provider
    // holding two disjoint spans of one production is the case this exists for)
    // and 409s on a whole-document link, which must be narrowed explicitly.
    attachPageRanges: (caseId, linkId, payload) => nexusApi.post(`/medical-evidence/${caseId}/document-links/${linkId}/page-ranges/`, payload),
    // Soft-removes one span; the row is tombstoned, never deleted.
    removePageRange: (caseId, linkId, rangeId) => nexusApi.delete(`/medical-evidence/${caseId}/document-links/${linkId}/page-ranges/${rangeId}/`),
    // The only way coverage changes. Responds with the payload plus
    // `coverage_diff`, whose `retracted` spans are the pages that just stopped
    // being attributed — show them, never swallow them.
    setCoverageMode: (caseId, linkId, payload) => nexusApi.post(`/medical-evidence/${caseId}/document-links/${linkId}/coverage-mode/`, payload),
    retrySource: (caseId) => nexusApi.post(`/medical-evidence/${caseId}/source/retry/`),
    // Any authenticated user of the owning firm — deliberately NOT admin-only.
    // 202 {status: "queued"} when the case is ready (the server dedups an
    // in-flight run, so a repeat click is still a 202); 409 {error: "not_ready",
    // blockers: [...]} when it is not, recomputed server-side — the blockers in
    // the body are fresher than the payload on screen.
    generateChronology: (caseId) => nexusApi.post(`/medical-evidence/${caseId}/generate-chronology/`),
    // Admin-only, FULL and irreversible: deletes every provider, link, page
    // range, records request, validation task (history included) and run for
    // the case and unpins the source bundle; documents are untouched and the
    // case rebuilds from scratch. The 202 body is the full refreshed payload
    // plus a `reset` object of per-table deletion counts. 409 means a discovery
    // run is in flight; retryable with `force: true`.
    resetCaseEvidence: (caseId, payload) => nexusApi.post(`/medical-evidence/${caseId}/reset/`, payload),
    resolveValidationTask: (taskId, payload) => nexusApi.post(`/medical-evidence/validation-tasks/${taskId}/resolve/`, payload),
    dismissValidationTask: (taskId, { reason }) => nexusApi.post(`/medical-evidence/validation-tasks/${taskId}/dismiss/`, { reason }),
    // Withdraws a dismissal AND un-fences every dismissed sibling sharing the
    // task's subject, so the underlying question can be raised again.
    reopenValidationTask: (taskId) => nexusApi.post(`/medical-evidence/validation-tasks/${taskId}/reopen/`)
};

const embeddingService = {
    enable: (caseId) => nexusApi.post('/vector-search/enable/', { case_id: caseId }),
    reindex: (caseId) => nexusApi.post('/vector-search/reindex/', { case_id: caseId }),
    forceReindex: (caseId) => nexusApi.post('/vector-search/force-reindex/', { case_id: caseId }),
    status: (caseId) => nexusApi.get(`/vector-search/status/${caseId}/`),
    search: (caseId, query, { sourceTypes = null, documentIds = null } = {}) =>
        nexusApi.post('/vector-search/search/', {
            case_id: caseId,
            query,
            ...(sourceTypes && { source_types: sourceTypes }),
            ...(documentIds && { document_ids: documentIds })
        }),
    coverage: (caseId) => nexusApi.get(`/vector-search/coverage/${caseId}/`),
    sourceMetadata: (caseId, sourceType, sourceId) =>
        nexusApi.get(`/vector-search/source-metadata/${caseId}/`, {
            params: { source_type: sourceType, source_id: sourceId }
        }),
    cases: (params) => nexusApi.get('/vector-search/cases/', { params }),
    enableBulk: (caseIds) => nexusApi.post('/vector-search/enable-bulk/', { case_ids: caseIds })
};

// Campaign service
export const campaignsService = {
    list: (params = {}) => nexusApi.get('/campaigns/', { params }),
    get: (id) => nexusApi.get(`/campaigns/${id}/`),
    create: (data) => nexusApi.post('/campaigns/', data),
    update: (id, data) => nexusApi.patch(`/campaigns/${id}/`, data),
    delete: (id) => nexusApi.delete(`/campaigns/${id}/`),
    activate: (id) => nexusApi.post(`/campaigns/${id}/activate/`),
    pause: (id) => nexusApi.post(`/campaigns/${id}/pause/`),
    execute: (id) => nexusApi.post(`/campaigns/${id}/execute/`),
    preview: (id) => nexusApi.post(`/campaigns/${id}/preview/`),
    executions: (id, params = {}) => nexusApi.get(`/campaigns/${id}/executions/`, { params }),
    messages: (id, params = {}) => nexusApi.get(`/campaigns/${id}/messages/`, { params }),
    stats: (id) => nexusApi.get(`/campaigns/${id}/stats/`),
    filterOptions: () => nexusApi.get('/campaigns/filter-options/'),
    // Enrollment roster (LAW-1117): persisted per-case enrollment with
    // sticky exclusions, manual adds, and a filter-sync backstop.
    syncFilter: (id) => nexusApi.post(`/campaigns/${id}/sync-filter/`),
    enrollments: (id, params = {}) => nexusApi.get(`/campaigns/${id}/enrollments/`, { params }),
    addEnrollments: (id, caseIds) => nexusApi.post(`/campaigns/${id}/enrollments/`, { case_ids: caseIds }),
    enrollmentUpdate: (id, enrollmentId, data) => nexusApi.patch(`/campaigns/${id}/enrollments/${enrollmentId}/`, data),
    enrollmentSummary: (id) => nexusApi.get(`/campaigns/${id}/enrollment-summary/`),
    // Blob-based CSV download so the request carries the auth token /
    // account header that a plain <a href> would drop.
    enrollmentsCsv: (id, params = {}) => nexusApi.get(`/campaigns/${id}/enrollments/`, { params: { ...params, export: 'csv' }, responseType: 'blob' })
};

// WCM controlled check-in rollout. These routes back the focused
// Admin → WCM Check-ins page instead of the generic seven-step builder.
//
// No account identifier is ever sent in a path or body: every queryset is
// scoped server-side from `request.account`, resolved from the auth token and
// the X-Account-ID header the request interceptor already attaches. The client
// must not be able to widen that scope, so it does not name it.
//
// `saveSetup` deliberately posts only the operator-owned fields. The case-type
// filter, `campaign_kind`, the SMS channel and the recurrence interval are
// owned by the backend — a client that could set them could turn a generic
// campaign into a WCM one, or the reverse. The approved recurrence is 120 days;
// the setup payload reports what the campaign is actually saved with through
// `schedule_unit`/`schedule_amount`, and the page renders that rather than
// asserting the rule over it (Prism ADR 0008).
export const wcmCheckinService = {
    getSetup: () => nexusApi.get('/campaigns/wcm-checkin/setup/'),
    saveSetup: (payload) => nexusApi.post('/campaigns/wcm-checkin/setup/', payload),
    rolloutPreview: (id, params = {}) => nexusApi.get(`/campaigns/${id}/rollout-preview/`, { params }),
    pause: (id) => nexusApi.post(`/campaigns/${id}/pause/`),
    listOfficeClosures: () => nexusApi.get('/campaigns/wcm-office-closures/'),
    createOfficeClosure: (payload) => nexusApi.post('/campaigns/wcm-office-closures/', payload),
    deactivateOfficeClosure: (id, previewVersion) => nexusApi.post(`/campaigns/wcm-office-closures/${id}/deactivate/`, { preview_version: previewVersion }),
    // Nexus derives reconciliation state while building the authoritative
    // rollout preview. There is no apply endpoint for Prism to call: both a
    // status read and an operator refresh therefore request a fresh preview.
    reconciliationStatus: (id) => nexusApi.get(`/campaigns/${id}/rollout-preview/`, { params: {} }),
    refreshReconciliation: (id) => nexusApi.get(`/campaigns/${id}/rollout-preview/`, { params: {} }),
    // Activation is a distinct route from the generic campaign activate: it
    // requires the version of the preview the operator actually reviewed, so a
    // configuration that changed after the preview cannot be activated blind.
    // The path is the backend's `url_path="wcm-activate"` (campaigns/views.py,
    // Nexus ADR 0036) — the router exposes no `wcm-checkin/activate` alias, so
    // this spelling is the contract, not a preference.
    activate: (id, previewVersion) => nexusApi.post(`/campaigns/${id}/wcm-activate/`, { preview_version: previewVersion })
};

// Campaign-response review queue (LAW-1119). Backend authorization limits
// non-admin users to responses for cases where they are the active manager.
//
// One endpoint, two questions, told apart entirely by the params
// (`CampaignResponseViewSet.filter_queryset`):
//   `{ campaign }`     — that campaign's complete history, every review status,
//                        newest first. Backs the dashboard Responses tab.
//   `{ needs_review }` — new/escalated across all campaigns, oldest first.
//                        Backs the global Needs Review queue.
// Sending both narrows the first to the second; see ADR 0008.
export const campaignResponsesService = {
    list: (params = {}) => nexusApi.get('/campaigns/responses/', { params }),
    setReviewStatus: (id, reviewStatus) =>
        nexusApi.patch(`/campaigns/responses/${id}/review-status/`, {
            review_status: reviewStatus
        })
};

// Ad management (AI Google Ads) — read-only DRF endpoints under
// /accounts/{account_id}/ad_management/. Backend was delivered in LAW-477.
// We resolve {account_id} per-call so a tab can stay on the right account
// even if `currentAccount` changes mid-session.
function resolveAccountId(accountId) {
    if (accountId) return accountId;
    try {
        const raw = localStorage.getItem('currentAccount');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.id ?? null;
    } catch {
        return null;
    }
}

function adManagementBase(accountId) {
    const id = resolveAccountId(accountId);
    if (!id) {
        throw new Error('No current account selected for ad-management API call');
    }
    return `/accounts/${id}/ad_management`;
}

export const adManagementService = {
    listDecisions: (accountId, params = {}) => nexusApi.get(`${adManagementBase(accountId)}/decisions/`, { params }),
    getDecision: (accountId, decisionId) => nexusApi.get(`${adManagementBase(accountId)}/decisions/${decisionId}/`),
    approveDecision: (accountId, decisionId, payload = {}) => nexusApi.post(`${adManagementBase(accountId)}/decisions/${decisionId}/approve/`, payload),
    rejectDecision: (accountId, decisionId, payload = {}) => nexusApi.post(`${adManagementBase(accountId)}/decisions/${decisionId}/reject/`, payload),
    getSafeguards: (accountId) => nexusApi.get(`${adManagementBase(accountId)}/safeguards/`),
    updateSafeguards: (accountId, patch) => nexusApi.patch(`${adManagementBase(accountId)}/safeguards/`, patch),
    engageKillSwitch: (accountId, payload = {}) => nexusApi.post(`${adManagementBase(accountId)}/kill-switch/engage/`, payload),
    disengageKillSwitch: (accountId, payload = {}) => nexusApi.post(`${adManagementBase(accountId)}/kill-switch/disengage/`, payload),
    listPolicies: (accountId, params = {}) => nexusApi.get(`${adManagementBase(accountId)}/policies/`, { params }),
    createPolicy: (accountId, payload) => nexusApi.post(`${adManagementBase(accountId)}/policies/`, payload),
    updatePolicy: (accountId, policyId, payload) => nexusApi.patch(`${adManagementBase(accountId)}/policies/${policyId}/`, payload),
    deletePolicy: (accountId, policyId) => nexusApi.delete(`${adManagementBase(accountId)}/policies/${policyId}/`),
    listRoleAssignments: (accountId, params = {}) => nexusApi.get(`${adManagementBase(accountId)}/role-assignments/`, { params }),
    createRoleAssignment: (accountId, payload) => nexusApi.post(`${adManagementBase(accountId)}/role-assignments/`, payload),
    updateRoleAssignment: (accountId, roleId, payload) => nexusApi.patch(`${adManagementBase(accountId)}/role-assignments/${roleId}/`, payload),
    deleteRoleAssignment: (accountId, roleId) => nexusApi.delete(`${adManagementBase(accountId)}/role-assignments/${roleId}/`),
    listModels: (accountId) => nexusApi.get(`${adManagementBase(accountId)}/models/`),
    runNow: (accountId, payload = {}) => nexusApi.post(`${adManagementBase(accountId)}/run-now/`, payload),
    // Anomalies (LAW-514): read-only feed + acknowledge/resolve lifecycle.
    listAnomalies: (accountId, params = {}) => nexusApi.get(`${adManagementBase(accountId)}/anomalies/`, { params }),
    getAnomaly: (accountId, anomalyId) => nexusApi.get(`${adManagementBase(accountId)}/anomalies/${anomalyId}/`),
    acknowledgeAnomaly: (accountId, anomalyId, payload = {}) => nexusApi.post(`${adManagementBase(accountId)}/anomalies/${anomalyId}/acknowledge/`, payload),
    resolveAnomaly: (accountId, anomalyId, payload = {}) => nexusApi.post(`${adManagementBase(accountId)}/anomalies/${anomalyId}/resolve/`, payload),
    // Marketing briefs (LAW-514): list + create.
    listBriefs: (accountId, params = {}) => nexusApi.get(`${adManagementBase(accountId)}/briefs/`, { params }),
    createBrief: (accountId, payload) => nexusApi.post(`${adManagementBase(accountId)}/briefs/`, payload)
};

// My Calendar: the user's synced Outlook events plus account-wide CRM (Neos)
// case events. Both list endpoints are DRF-paginated ({count, results, next,
// previous}) and take start/end + account_user (AccountUser pk) filters.
export const calendarService = {
    // Outlook events pulled from Microsoft Graph (calendars app)
    getUserCalendarEvents: (params = {}) => nexusApi.get('/calendars/calendar-events/', { params }),
    // Link-only writes: { case: id|null } or { lead: id|null }
    patchUserCalendarEvent: (id, data) => nexusApi.patch(`/calendars/calendar-events/${id}/`, data),
    // Sync-enabled account users for the calendar picker: [{id (AccountUser pk), user_id, email, name}]
    getCalendarUsers: () => nexusApi.get('/calendars/users/'),
    // CRM (Neos) case events, account-wide; start and end are required
    getAccountCalendarEvents: (params = {}) => nexusApi.get('/crm/calendar-events/', { params })
};

export const courtDayService = {
    list: (params = {}) => nexusApi.get('/court-days/', { params }),
    getDay: (courtDate, params = {}) => nexusApi.get(`/court-days/${courtDate}/`, { params }),
    // Six-week planning schedule from the calendar mirror: params { attorney_id?, start? (a Monday, YYYY-MM-DD), weeks? (only 6) }
    getSchedule: (params = {}) => nexusApi.get('/court-days/schedule/', { params }),
    saveStrategyNote: (courtDate, itemId, data) => nexusApi.patch(`/court-days/${courtDate}/items/${itemId}/strategy-note/`, data),
    saveInstruction: (courtDate, itemId, data) => nexusApi.patch(`/court-days/${courtDate}/items/${itemId}/instruction/`, data),
    createInstructionBatch: (courtDate, data) => nexusApi.post(`/court-days/${courtDate}/instruction-batches/`, data, { __skipRetry: true }),
    getInstructionBatch: (courtDate, batchId) => nexusApi.get(`/court-days/${courtDate}/instruction-batches/${batchId}/`),
    getInstructionBatchByKey: (courtDate, key) => nexusApi.get(`/court-days/${courtDate}/instruction-batches/`, { params: { idempotency_key: key } }),
    createNextHearing: (courtDate, itemId, data) => nexusApi.post(`/court-days/${courtDate}/items/${itemId}/next-hearing-submissions/`, data, { __skipRetry: true }),
    getNextHearing: (courtDate, itemId, submissionId) => nexusApi.get(`/court-days/${courtDate}/items/${itemId}/next-hearing-submissions/${submissionId}/`),
    getNextHearingByKey: (courtDate, itemId, key) => nexusApi.get(`/court-days/${courtDate}/items/${itemId}/next-hearing-submissions/`, { params: { idempotency_key: key } }),
    listAppointmentTypes: () => nexusApi.get('/court-days/appointment-types/'),
    // Appointment work queue: params { state: 'open'|'closed', attorney_id?, limit?, cursor?, location?, hearing_type?, missing?, segment?, court_date?, q? }
    getWorkItems: (params = {}) => nexusApi.get('/court-days/work-items/', { params }),
    // Close one hearing: data { idempotency_key, instruction_revision_id, next_hearing_submission_id }
    closeItem: (courtDate, itemId, data) => nexusApi.post(`/court-days/${courtDate}/items/${itemId}/closure/`, data, { __skipRetry: true }),
    getClosure: (courtDate, itemId) => nexusApi.get(`/court-days/${courtDate}/items/${itemId}/closure/`)
};

// Update the default export to include the document service
export default {
    firebase: firebaseAuthService,
    user: userService,
    email: emailService,
    crm: crmService,
    document: documentService,
    steps: stepsService,
    mail: mailService,
    accounts: accountsService,
    fax: faxService,
    scan: scanService,
    auth: authHelpers,
    reports: reportsService,
    notifications: notificationsService,
    ai: aiService,
    pdfForms: pdfFormsService,
    callTracking: callTrackingService,
    embedding: embeddingService,
    rag: ragService,
    generatedDocument: generatedDocumentService,
    medicalChronology: medicalChronologyService,
    medicalEvidence: medicalEvidenceService,
    campaigns: campaignsService,
    adManagement: adManagementService,
    calendar: calendarService,
    courtDays: courtDayService
};
